import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { InstallAppBanner } from "@/hooks/usePWAManifest";
import { adminApi, api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChefHat,
  ClipboardList,
  Utensils,
  Settings,
  Gift,
  Download,
  LogOut,
  RefreshCw,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  Home as HomeIcon,
  BellRing,
  Copy,
  Power,
  PowerOff,
  Upload,
  MessageCircle,
  Star,
  Reply,
  Send,
  Thermometer,
  Printer,
  Mail,
  ChevronDown,
  Sparkles,
  CreditCard,
  Users,
  Clock,
  TrendingUp,
  TrendingDown,
  Briefcase,
  Package,
} from "lucide-react";
import { InventoryTab } from "@/components/inventory/InventoryTab";
import { PromotionsCard } from "@/components/admin/PromotionsCard";
import { RecipeMappingTab } from "@/components/inventory/RecipeMappingTab";
import JobApplicationsTab from "@/components/admin/JobApplicationsTab";
import { CustomThemesEditor } from "@/components/admin/CustomThemesEditor";
import { SettingsShell } from "@/components/admin/SettingsShell";
import { PointsCreditCard } from "@/components/admin/PointsCreditCard";
import { VideoLibraryCard } from "@/components/admin/VideoLibraryCard";
import { ClockAppCard } from "@/components/admin/ClockAppCard";
import { TimeEntriesCard, TimeEntryEditor } from "@/components/admin/TimeEntriesCard";
import { WeeklyPayCard } from "@/components/admin/WeeklyPayCard";
import { RangePayCard } from "@/components/admin/RangePayCard";
import { CloseShiftButton } from "@/components/admin/CloseShiftButton";
import { DailyPayCard } from "@/components/admin/DailyPayCard";
import SoundtrackCard from "@/components/admin/SoundtrackCard";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const TOKEN_KEY = "bk_admin_token";
const STATUS_FLOW = ["Received", "Preparing", "Ready", "Completed"];
const FALLBACK_CATEGORIES = ["Burgers", "Sandwiches", "Loaded Fries", "Vegetarian", "Wraps", "Drinks", "Add-Ons"];

/** Live categories from site_settings, falling back to the built-in list
 * on very first load before settings have arrived. */
function useMenuCategories() {
  const { settings } = useSiteSettings();
  return (settings?.menu_categories && settings.menu_categories.length)
    ? settings.menu_categories
    : FALLBACK_CATEGORIES;
}

// ---------------- Orders Open master toggle (floating in admin header) --
// One-tap kill switch for online ordering. When OFF, /orders/pay refuses
// new orders with a friendly message. Poll every 30s to reflect out-of-band
// changes (e.g. staff toggled from another device) so the button never
// lies about the current state.
function OrdersOpenToggle({ token }) {
  const { settings, refresh } = useSiteSettings();
  const [saving, setSaving] = useState(false);
  const isOpen = settings?.orders_open !== false;

  const flip = async () => {
    const nextOpen = !isOpen;
    if (!nextOpen) {
      if (!window.confirm(
        "Pause online ordering? Customers can still browse the menu but will see a closed banner. In-person orders at the truck still work."
      )) return;
    }
    setSaving(true);
    try {
      await adminApi(token).put("/site-settings", { orders_open: nextOpen });
      await refresh();
      toast.success(
        nextOpen ? "Online ordering is open" : "Online ordering paused"
      );
    } catch {
      toast.error("Couldn't change ordering state");
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={flip}
      disabled={saving}
      data-testid="admin-orders-toggle"
      title={isOpen ? "Tap to pause online ordering" : "Tap to reopen online ordering"}
      className={`text-[10px] uppercase tracking-widest font-black px-3 py-2 rounded-full flex items-center gap-1.5 border-2 transition-all active:scale-95 disabled:opacity-50 ${
        isOpen
          ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
          : "bg-red-600 text-white border-red-600 hover:bg-red-700 animate-ready-glow"
      }`}
    >
      {isOpen ? (
        <>
          <Power className="w-3.5 h-3.5" /> Ordering Open
        </>
      ) : (
        <>
          <PowerOff className="w-3.5 h-3.5" /> Ordering Paused
        </>
      )}
    </button>
  );
}



export default function AdminPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [tab, setTab] = useState("orders");
  const [settingsJump, setSettingsJump] = useState(null);
  const [hasCustomPassword, setHasCustomPassword] = useState(true);
  const [chatUnread, setChatUnread] = useState(0);
  const [jobsNew, setJobsNew] = useState(0);

  // Poll global chat-thread unread count every 15s so the header badge
  // stays live no matter which tab the owner is on.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await adminApi(token).get("/admin/chat/threads");
        if (!cancelled) setChatUnread(data.total_unread || 0);
      } catch {
        /* ignore transient errors */
      }
      try {
        const { data } = await adminApi(token).get("/admin/job-applications/new-count");
        if (!cancelled) setJobsNew(data.count || 0);
      } catch {
        /* ignore transient errors */
      }
    };
    pull();
    const t = setInterval(pull, 15000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [token, tab]);

  // Fetch admin status on mount + after any password change
  useEffect(() => {
    if (!token) return;
    adminApi(token)
      .get("/admin/status")
      .then((r) => setHasCustomPassword(!!r.data.has_custom_password))
      .catch(() => {});
  }, [token]);

  // HARD role gate — cashier tokens can't view the Admin dashboard at all.
  // If a cashier navigates directly to /admin (or has a cached cashier
  // session), we bounce them to the POS with a toast so there's no way to
  // even see the layout of the admin UI.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    adminApi(token)
      .get("/admin/me")
      .then(({ data }) => {
        if (cancelled) return;
        if (data.role !== "admin") {
          toast.error(data.role === "manager" ? "Managers use the Manager app." : "Admin only — sign in with the admin passcode.");
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem("bk_admin_role");
          setToken("");
          navigate(data.role === "manager" ? "/manager" : "/pos", { replace: true });
        }
      })
      .catch(() => {
        // ignore — the /orders check below will surface auth errors
      });
    return () => { cancelled = true; };
  }, [token, navigate]);

  // Verify the localStorage token is still valid whenever it's set on mount.
  // If the password was rotated (e.g. from another device), the stored token
  // is stale — auto-logout so the user re-enters the current passcode instead
  // of hitting confusing "Unauthorized" errors deep in the app.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    adminApi(token)
      .get("/orders")
      .then(() => {})
      .catch((err) => {
        if (!cancelled && err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          toast.error("Session expired — please log in again");
        }
      });
    return () => {
      cancelled = true;
    };
    // Only run on mount and when token changes to a new value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { passcode });
      if (data.role !== "admin") {
        // Cashier passcode was correct but this page is admin-only.
        toast.error("Admin only — that's the cashier passcode.");
        // Immediately revoke the session we just minted; no reason to
        // leave a live cashier token dangling on the admin device.
        try {
          await api.post("/admin/logout", null, {
            headers: { "x-admin-token": data.token },
          });
        } catch { /* noop */ }
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem("bk_admin_role", "admin");
      setToken(data.token);
      setHasCustomPassword(!!data.has_custom_password);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid passcode");
    }
  };

  const logout = () => {
    // Best-effort: revoke the session server-side. If it's already an old
    // raw passcode token this is a no-op.
    if (token) {
      adminApi(token).post("/admin/logout").catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("bk_admin_role");
    setTab("orders");
    setToken("");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
        <InstallAppBanner
          manifest="/manifest-admin.json"
          themeColor="#1c1917"
          appleTitle="BK Admin"
          label="BK Admin"
        />
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-stone-200 p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-brand-red flex items-center justify-center mb-3">
              <ChefHat className="w-7 h-7 text-brand-yellow" />
            </div>
            <div className="font-display text-2xl text-brand-red">Blossom&apos;s Admin</div>
            <div className="text-xs text-stone-500 uppercase tracking-widest">
              Kitchen dashboard
            </div>
          </div>
          <form onSubmit={login} className="space-y-3">
            <input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              required
              data-testid="admin-login-input"
              className="w-full px-3 py-2.5 rounded-lg border border-stone-300 outline-none focus:border-brand-red text-sm"
            />
            <button
              type="submit"
              data-testid="admin-login-submit"
              className="w-full bg-brand-red text-white py-2.5 rounded-full font-bold uppercase text-xs tracking-widest hover:bg-red-800"
            >
              Enter Kitchen
            </button>
            <Link
              to="/"
              className="block text-center text-xs text-stone-400 hover:text-stone-600 pt-2"
            >
              ← Back to customer app
            </Link>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen bg-stone-100 flex flex-col overflow-hidden"
      data-testid="admin-page"
    >
      <InstallAppBanner
        manifest="/manifest-admin.json"
        themeColor="#1c1917"
        appleTitle="BK Admin"
        label="BK Admin"
      />
      <div className="chalkboard-band border-b-4 border-brand-red shrink-0">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-brand-red flex items-center justify-center shrink-0">
              <ChefHat className="w-5 h-5 text-brand-yellow" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-brand-yellow text-lg sm:text-xl leading-none truncate">
                Blossom&apos;s Kitchen
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/70 mt-0.5">
                Admin Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
            <OrdersOpenToggle token={token} />
            <PwaInstallButton />
            <Link
              to="/kitchen"
              title="Kitchen display"
              aria-label="Kitchen display"
              className="text-white/80 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-2 sm:px-3 py-2"
              data-testid="admin-kitchen-link"
            >
              <ChefHat className="w-4 h-4" /> <span className="hidden sm:inline">Kitchen</span>
            </Link>
            <Link
              to="/pos"
              title="Point of sale"
              aria-label="Point of sale"
              className="text-xs uppercase tracking-widest flex items-center gap-1 px-2 sm:px-3 py-2 bg-brand-red rounded-full text-white"
              data-testid="admin-pos-link"
            >
              <CreditCard className="w-4 h-4" /> <span className="hidden sm:inline">POS</span>
            </Link>
            <Link
              to="/"
              title="Storefront"
              aria-label="Storefront"
              className="text-white/80 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-2 sm:px-3 py-2"
              data-testid="admin-home-link"
            >
              <HomeIcon className="w-4 h-4" /> <span className="hidden sm:inline">Storefront</span>
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              title="Log out"
              aria-label="Log out"
              className="text-white/80 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-2 sm:px-3 py-2"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
        <div
          className="max-w-6xl mx-auto px-3 sm:px-6 flex flex-wrap gap-1"
          data-testid="admin-tabs-scroll"
        >
          {[
            { key: "orders", label: "Orders", Icon: ClipboardList },
            { key: "menu", label: "Menu", Icon: Utensils },
            { key: "inventory", label: "Inventory", Icon: Package },
            { key: "recipes", label: "Recipes", Icon: ChefHat },
            { key: "chat", label: "Chat", Icon: MessageCircle, badge: chatUnread },
            { key: "reviews", label: "Reviews", Icon: Star },
            { key: "rewards", label: "Rewards", Icon: Gift },
            { key: "temp", label: "Temp Log", Icon: Thermometer },
            { key: "staff", label: "Staff", Icon: Users },
            { key: "jobs", label: "Jobs", Icon: Briefcase, badge: jobsNew },
            { key: "site", label: "Settings", Icon: Settings },
          ].map(({ key, label, Icon, badge }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`relative shrink-0 whitespace-nowrap px-3 sm:px-4 py-2.5 text-xs uppercase tracking-widest font-bold flex items-center gap-2 rounded-t-lg ${
                tab === key
                  ? "bg-[#FDFBF7] text-brand-red"
                  : "text-white/70 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
              {badge > 0 && (
                <span
                  data-testid={`admin-tab-${key}-badge`}
                  className="absolute -top-1 -right-1 bg-brand-yellow text-brand-red font-black text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center border-2 border-brand-red"
                >
                  {badge > 9 ? "9+" : badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-3 sm:p-6 flex-1 overflow-y-auto w-full">
        {!hasCustomPassword && (
          <button
            onClick={() => { setSettingsJump("security"); setTab("site"); }}
            data-testid="admin-set-password-banner"
            className="w-full mb-4 bg-amber-100 border-2 border-amber-400 rounded-xl px-4 py-3 text-left flex items-center gap-3 hover:bg-amber-200 transition-colors"
          >
            <div className="w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0 font-black">!</div>
            <div className="flex-1">
              <div className="font-black text-amber-900 text-sm uppercase tracking-widest">
                Set a secure password
              </div>
              <div className="text-xs text-amber-800">
                You&apos;re using the default passcode. Tap here to open Settings → Security and lock down admin access.
              </div>
            </div>
          </button>
        )}
        {tab === "orders" ? (
          <OrdersTab token={token} />
        ) : tab === "menu" ? (
          <MenuTab token={token} />
        ) : tab === "inventory" ? (
          <InventoryTab token={token} />
        ) : tab === "recipes" ? (
          <RecipeMappingTab token={token} />
        ) : tab === "rewards" ? (
          <RewardsTab token={token} />
        ) : tab === "chat" ? (
          <ChatTab token={token} />
        ) : tab === "reviews" ? (
          <ReviewsTab token={token} />
        ) : tab === "temp" ? (
          <TempLogTab token={token} />
        ) : tab === "staff" ? (
          <StaffTab token={token} />
        ) : tab === "jobs" ? (
          <JobApplicationsTab token={token} />
        ) : (
          <SiteTab
            token={token}
            jumpTo={settingsJump}
            hasCustomPassword={hasCustomPassword}
            onPasswordChanged={(newToken) => {
              localStorage.setItem(TOKEN_KEY, newToken);
              setToken(newToken);
              setHasCustomPassword(true);
            }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------- Orders Tab ----------------


function OrdersTab({ token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [msgTarget, setMsgTarget] = useState(null);

  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, statsRes] = await Promise.all([
        adminApi(token).get("/orders"),
        adminApi(token).get("/admin/order-stats").catch(() => null),
      ]);
      setOrders(data);
      if (statsRes) setStats(statsRes.data);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi(token).put(`/orders/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const filtered = orders.filter((o) => {
    if (filter === "active") return ["Received", "Preparing", "Ready"].includes(o.status);
    if (filter === "completed") return o.status === "Completed";
    if (filter === "cancelled") return o.status === "Cancelled";
    return true;
  });

  const today = stats?.today;
  const allTime = stats?.all_time;
  const fmtMoney = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Today · Orders</div>
        <CloseShiftButton token={token} compact />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Active" value={allTime ? allTime.active : "—"} accent="red" testId="orders-stat-active" />
        <StatCard
          label="Completed today"
          value={today ? today.completed : "—"}
          accent="green"
          sub={allTime ? `${allTime.completed} total` : null}
          testId="orders-stat-completed"
        />
        <StatCard
          label="Revenue today"
          value={today ? fmtMoney(today.revenue) : "—"}
          accent="yellow"
          sub={allTime ? `${fmtMoney(allTime.revenue)} total` : null}
          testId="orders-stat-revenue"
        />
      </div>

      <MenuTrendCard token={token} />

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2">
          {["active", "completed", "cancelled", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`orders-filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold border-2 ${
                filter === f
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          data-testid="orders-refresh-btn"
          className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-stone-400 font-hand text-3xl text-center py-10">
          Loading orders...
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-stone-200 p-10 text-center text-stone-400">
          <div className="font-hand text-2xl">No orders here</div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((o) => (
            <div
              key={o.id}
              data-testid={`admin-order-card-${o.order_number}`}
              className="bg-white border border-stone-200 rounded-xl p-4 space-y-3"
            >
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="font-display text-brand-red text-xl leading-none">
                    {o.order_number}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">
                    {o.customer_name} · {o.phone}
                    {o.curbside && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-brand-yellow text-stone-900 font-black uppercase tracking-widest" data-testid={`admin-curbside-${o.order_number}`}>🚗 Curbside{o.curbside.checked_in_at ? " · HERE" : ""}{o.curbside.vehicle ? ` · ${o.curbside.vehicle}` : ""}{o.curbside.spot ? ` · ${o.curbside.spot}` : ""}</span>}
                  </div>
                </div>
                <StatusBadge status={o.status} />
              </div>
              {o.payment_status === "offline_pending" && (
                <div
                  data-testid={`admin-offline-badge-${o.order_number}`}
                  className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 font-black uppercase tracking-widest"
                  title="Card taken offline on the reader — Square will confirm when the device is back online"
                >
                  <Clock className="w-3 h-3" /> Awaiting Square confirmation
                </div>
              )}
              <div className="text-xs text-stone-600">
                Pickup: <span className="font-bold">{o.pickup_time}</span>
              </div>
              <ul className="text-sm space-y-0.5 border-t border-stone-100 pt-2">
                {o.items.map((it) => (
                  <li key={it.item_id} className="flex justify-between">
                    <span className="text-stone-700 truncate mr-2">
                      <span className="font-bold">{it.quantity}×</span> {it.name}
                    </span>
                    <span className="text-stone-500 whitespace-nowrap">
                      ${(it.price * it.quantity).toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
              {o.notes && (
                <div className="text-xs bg-brand-yellow/20 border border-brand-yellow/40 rounded p-2 text-stone-700">
                  <span className="font-bold">Notes:</span> {o.notes}
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                <span className="font-display text-brand-red">
                  ${(o.total || o.subtotal).toFixed(2)}
                </span>
                <div className="flex items-center gap-2">
                  {o.phone && (
                    <button
                      onClick={() => setMsgTarget(o)}
                      data-testid={`admin-message-customer-${o.order_number}`}
                      title="Message this customer"
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black text-brand-red border border-brand-red rounded-full px-2.5 py-1.5 hover:bg-brand-red hover:text-white transition-colors"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> Message
                    </button>
                  )}
                  <StatusActions order={o} onUpdate={updateStatus} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {msgTarget && (
        <MessageCustomerModal
          token={token}
          order={msgTarget}
          onClose={() => setMsgTarget(null)}
        />
      )}
    </div>
  );
}

function MessageCustomerModal({ token, order, onClose }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await adminApi(token).post("/admin/chat/threads", {
        phone: order.phone,
        name: order.customer_name || "Customer",
        order_number: order.order_number || "",
        message: t,
      });
      toast.success("Message sent — the customer will see it in chat");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send message");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-sm p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-message-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              Message {order.order_number}
            </div>
            <h2 className="font-display text-brand-red text-xl leading-none mt-0.5">
              {order.customer_name || "Customer"} · {order.phone}
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-stone-500" /></button>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Your order's ready for pickup!"
          data-testid="admin-message-textarea"
          autoFocus
          rows={3}
          className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm resize-none"
        />
        <button
          onClick={send}
          disabled={sending || !text.trim()}
          data-testid="admin-message-send-btn"
          className="w-full py-2.5 rounded-lg bg-brand-red text-white font-black text-xs uppercase tracking-widest hover:bg-red-800 disabled:opacity-40 flex items-center justify-center gap-2"
        >
          <MessageCircle className="w-4 h-4" />
          {sending ? "Sending…" : "Send message"}
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, sub, testId }) {
  const color =
    accent === "red"
      ? "text-brand-red"
      : accent === "green"
      ? "text-emerald-700"
      : "text-yellow-700";
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">
        {label}
      </div>
      <div className={`font-display text-3xl mt-1 ${color}`}>{value}</div>
      {sub && (
        <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-1 leading-tight" data-testid={testId ? `${testId}-alltime` : undefined}>
          {sub}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Received: "bg-blue-100 text-blue-800",
    Preparing: "bg-amber-100 text-amber-800",
    Ready: "bg-emerald-100 text-emerald-800",
    Completed: "bg-stone-200 text-stone-700",
    Cancelled: "bg-red-100 text-red-800",
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${map[status]}`}
    >
      {status}
    </span>
  );
}

function StatusActions({ order, onUpdate }) {
  const idx = STATUS_FLOW.indexOf(order.status);
  const next = idx >= 0 && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
  const isTerminal = ["Completed", "Cancelled"].includes(order.status);
  return (
    <div className="flex gap-1.5">
      {!isTerminal && next && (
        <button
          onClick={() => onUpdate(order.id, next)}
          data-testid={`admin-update-status-btn-${order.order_number}`}
          className="bg-brand-red text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full hover:bg-red-800 flex items-center gap-1"
        >
          <Check className="w-3 h-3" /> {next}
        </button>
      )}
      {!isTerminal && (
        <button
          onClick={() => onUpdate(order.id, "Cancelled")}
          data-testid={`admin-cancel-btn-${order.order_number}`}
          className="bg-stone-200 text-stone-700 text-[10px] uppercase tracking-widest font-bold px-2 py-1.5 rounded-full hover:bg-stone-300"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ---------------- Waitlist Modal (per-item) ----------------
function WaitlistModal({ token, entry, onClose, onCleared }) {
  const [clearing, setClearing] = useState(false);
  const subs = entry.subscribers || [];

  const fmtPhone = (d) => {
    const s = (d || "").replace(/\D/g, "");
    if (s.length !== 10) return d;
    return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
  };

  const copyAll = () => {
    const text = subs.map((s) => fmtPhone(s.phone)).join(", ");
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${subs.length} numbers`);
  };

  const clear = async () => {
    if (!window.confirm(
      `Clear the waitlist for "${entry.item_name}"? Do this AFTER you've texted everyone.`
    )) return;
    setClearing(true);
    try {
      await adminApi(token).delete(`/admin/restock-waitlist/${entry.item_id}`);
      toast.success("Waitlist cleared");
      onCleared();
    } catch {
      toast.error("Couldn't clear");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="admin-waitlist-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="chalkboard-band px-4 py-3 flex items-center justify-between border-b-4 border-brand-red">
          <div className="flex items-center gap-2">
            <BellRing className="w-5 h-5 text-brand-yellow" />
            <div className="font-display text-brand-yellow text-lg leading-tight">
              {entry.count} waiting on {entry.item_name}
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-stone-500">
            Text these customers from your own phone when the item is back. Tap{" "}
            <span className="font-bold">Copy all</span> to grab every number at once,
            then paste into Messages.
          </p>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-stone-200 divide-y divide-stone-100 bg-stone-50">
            {subs.map((s, i) => (
              <div
                key={s.phone + i}
                className="flex items-center justify-between px-3 py-2 text-sm"
                data-testid={`admin-waitlist-row-${i}`}
              >
                <a
                  href={`sms:+1${s.phone}?body=${encodeURIComponent(
                    `Hey${s.name ? " " + s.name : ""}! ${entry.item_name} is back on the truck — come grab one! — Blossom's Kitchen`
                  )}`}
                  className="font-mono text-brand-red font-bold hover:underline"
                >
                  {fmtPhone(s.phone)}
                </a>
                {s.name && <span className="text-stone-500 text-xs">{s.name}</span>}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={copyAll}
              className="flex-1 bg-stone-900 text-white py-2 rounded-full font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-1"
              data-testid="admin-waitlist-copy-all"
            >
              <Copy className="w-3.5 h-3.5" /> Copy all
            </button>
            <button
              onClick={clear}
              disabled={clearing}
              className="flex-1 bg-brand-red text-white py-2 rounded-full font-bold uppercase tracking-widest text-xs disabled:opacity-40"
              data-testid="admin-waitlist-clear"
            >
              {clearing ? "Clearing…" : "Clear list"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------- Menu Tab ----------------
/**
 * ExpiredLtoBanner — warns the owner when any menu item's Limited-Time
 * Offer has already passed its end date. The item stays visible on the
 * customer menu (per the "prompt the admin to decide" policy) but the
 * red LIMITED ribbon is gone. This banner offers three one-tap actions:
 *   • Extend +3 days  — bumps `lto_ends_at` to 3 days from now
 *   • Remove LTO tag  — clears `lto_active` + related fields
 *   • Review          — opens the item's edit form
 */
function ExpiredLtoBanner({ items, token, onEdit, onRefresh }) {
  const now = Date.now();
  const expired = (items || []).filter((it) => {
    if (!it.lto_active) return false;
    if (!it.lto_ends_at) return false;
    const end = new Date(it.lto_ends_at).getTime();
    return !Number.isNaN(end) && end <= now;
  });
  if (expired.length === 0) return null;

  const extend = async (item) => {
    const three = new Date(Date.now() + 3 * 86400_000).toISOString();
    try {
      await adminApi(token).put(`/menu/${item.id}`, { lto_ends_at: three });
      toast.success(`${item.name}: LTO extended 3 days`);
      onRefresh();
    } catch {
      toast.error("Couldn't extend");
    }
  };
  const clearTag = async (item) => {
    try {
      await adminApi(token).put(`/menu/${item.id}`, {
        lto_active: false,
        lto_ends_at: null,
        lto_price: null,
      });
      toast.success(`${item.name}: LTO removed`);
      onRefresh();
    } catch {
      toast.error("Couldn't remove");
    }
  };

  return (
    <div
      data-testid="admin-expired-lto-banner"
      className="bg-red-50 border-2 border-red-300 rounded-xl p-3 space-y-2"
    >
      <div className="flex items-center gap-2 text-sm font-black text-red-800 uppercase tracking-widest">
        <span>⏰</span>
        {expired.length} limited-time offer{expired.length > 1 ? "s" : ""} expired
      </div>
      <div className="text-xs text-red-700">
        These items are still on the menu but the countdown banner is hidden.
        Decide what to do with each:
      </div>
      <div className="space-y-1.5">
        {expired.map((it) => (
          <div
            key={it.id}
            data-testid={`admin-expired-lto-row-${it.id}`}
            className="bg-white rounded-lg border border-red-200 px-3 py-2 flex items-center justify-between gap-2 flex-wrap"
          >
            <div className="min-w-0">
              <div className="font-bold text-stone-800 truncate">{it.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">
                Ended {new Date(it.lto_ends_at).toLocaleString()}
              </div>
            </div>
            <div className="flex gap-1 flex-wrap">
              <button
                onClick={() => extend(it)}
                data-testid={`admin-expired-lto-extend-${it.id}`}
                className="px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Extend +3 days
              </button>
              <button
                onClick={() => clearTag(it)}
                data-testid={`admin-expired-lto-clear-${it.id}`}
                className="px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold bg-stone-600 text-white hover:bg-stone-700"
              >
                Remove LTO tag
              </button>
              <button
                onClick={() => onEdit(it.id)}
                data-testid={`admin-expired-lto-edit-${it.id}`}
                className="px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold border border-brand-red text-brand-red hover:bg-brand-red hover:text-white"
              >
                Review
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function MenuTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [waitlist, setWaitlist] = useState({}); // { item_id: {count, subscribers} }
  const [waitlistOpenFor, setWaitlistOpenFor] = useState(null);
  const [foodCost, setFoodCost] = useState({}); // { menu_item_id: {cost, margin_pct, missing_cost} }
  const CATEGORIES = useMenuCategories();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/menu");
      setItems(data);
      adminApi(token).get("/inventory/food-cost").then((r) => {
        setFoodCost(Object.fromEntries(r.data.filter((x) => x.mapped).map((x) => [x.menu_item_id, x])));
      }).catch(() => {});
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadWaitlist = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get("/admin/restock-waitlist");
      const byId = {};
      for (const row of data.items || []) byId[row.item_id] = row;
      setWaitlist(byId);
    } catch {
      /* non-critical */
    }
  }, [token]);

  useEffect(() => {
    load();
    loadWaitlist();
  }, [load, loadWaitlist]);

  // All available Add-On items (used by Edit/Add forms to render checkboxes)
  const addOns = useMemo(
    () => items.filter((i) => i.category === "Add-Ons"),
    [items]
  );

  const toggle = async (item) => {
    try {
      await adminApi(token).put(`/menu/${item.id}`, { available: !item.available });
      await load();
      // If the item just came back, admin will want to see the waitlist. Popping the
      // modal here is too aggressive — instead just refresh the count so the badge
      // is up-to-date, and let them tap it.
      loadWaitlist();
    } catch {
      toast.error("Failed");
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await adminApi(token).delete(`/menu/${item.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-4">
      <ExpiredLtoBanner
        items={items}
        token={token}
        onEdit={(id) => setEditingId(id)}
        onRefresh={load}
      />
      <div className="flex items-center justify-between">
        <div className="text-sm text-stone-600">
          {items.length} items on the menu
        </div>
        <div className="flex items-center gap-2">
          <ReoptimizeImagesButton token={token} />
          <button
            onClick={() => setShowAdd(true)}
            data-testid="admin-add-item-btn"
            className="bg-brand-red text-white text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-full hover:bg-red-800 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add Item
          </button>
        </div>
      </div>

      {showAdd && (
        <AddItemForm
          token={token}
          addOns={addOns}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}

      {waitlistOpenFor && waitlist[waitlistOpenFor] && (
        <WaitlistModal
          token={token}
          entry={waitlist[waitlistOpenFor]}
          onClose={() => setWaitlistOpenFor(null)}
          onCleared={() => {
            setWaitlistOpenFor(null);
            loadWaitlist();
          }}
        />
      )}

      {loading ? (
        <div className="text-stone-400 font-hand text-3xl text-center py-10">
          Loading menu...
        </div>
      ) : (
        CATEGORIES.map((cat) => {
          const catItems = items.filter((i) => i.category === cat);
          if (catItems.length === 0) return null;
          return (
            <div key={cat} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
              <div className="chalkboard-band px-4 py-2">
                <div className="font-display text-brand-yellow text-lg">{cat}</div>
              </div>
              <ul className="divide-y divide-stone-100">
                {catItems.map((item) => (
                  <li
                    key={item.id}
                    data-testid={`admin-menu-row-${item.id}`}
                  >
                    {editingId === item.id ? (
                      <EditItemForm
                        token={token}
                        item={item}
                        addOns={addOns}
                        onClose={() => setEditingId(null)}
                        onSaved={() => {
                          setEditingId(null);
                          load();
                        }}
                      />
                    ) : (
                      <div className="px-4 py-3 flex items-center gap-3">
                        {(() => {
                          const needsPhoto =
                            item.category !== "Drinks" &&
                            item.category !== "Add-Ons";
                          if (!needsPhoto) return null;
                          return item.image_url ? (
                            <img
                              src={item.image_url}
                              alt=""
                              loading="lazy"
                              data-testid={`admin-menu-thumb-${item.id}`}
                              className="w-12 h-12 rounded-lg object-cover border border-stone-200 shrink-0"
                            />
                          ) : (
                            <div
                              data-testid={`admin-menu-nophoto-${item.id}`}
                              title="No photo — customers see a text-only card. Upload one to make it visual."
                              className="w-12 h-12 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 text-[9px] uppercase tracking-widest font-black text-stone-400 flex items-center justify-center text-center leading-tight shrink-0"
                            >
                              No<br />photo
                            </div>
                          );
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-stone-800 truncate">
                            {item.name}
                          </div>
                          <div className="text-xs text-stone-500 truncate">
                            {item.description}
                          </div>
                          {!item.image_url &&
                            item.category !== "Drinks" &&
                            item.category !== "Add-Ons" && (
                              <div
                                data-testid={`admin-menu-hidden-badge-${item.id}`}
                                className="text-[10px] uppercase tracking-widest font-black text-stone-500 mt-0.5"
                              >
                                No photo yet
                              </div>
                            )}
                        </div>
                        <div className="w-20 text-right">
                          <div className="font-display text-brand-red text-lg">${item.price.toFixed(2)}</div>
                          {foodCost[item.id] && (
                            <div className={`text-[10px] font-bold tabular-nums ${foodCost[item.id].missing_cost?.length ? "text-amber-600" : "text-stone-500"}`} data-testid={`menu-food-cost-${item.id}`} title={foodCost[item.id].missing_cost?.length ? `No cost set for: ${foodCost[item.id].missing_cost.join(", ")}` : "Ingredient cost · margin"}>
                              cost ${foodCost[item.id].cost.toFixed(2)}{foodCost[item.id].margin_pct != null ? ` · ${foodCost[item.id].margin_pct}%` : ""}
                            </div>
                          )}
                        </div>
                        {waitlist[item.id]?.count > 0 && (
                          <button
                            type="button"
                            onClick={() => setWaitlistOpenFor(item.id)}
                            data-testid={`admin-waitlist-badge-${item.id}`}
                            title="View waiting customers"
                            className="text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded-full bg-brand-yellow/30 text-stone-900 border border-brand-yellow hover:bg-brand-yellow/60 flex items-center gap-1"
                          >
                            <BellRing className="w-3 h-3" />
                            {waitlist[item.id].count} waiting
                          </button>
                        )}
                        <button
                          onClick={() => toggle(item)}
                          data-testid={`admin-toggle-${item.id}`}
                          title={item.available ? "Click to mark this item Sold Out (customers can't order it)" : "Click to mark this item Available again"}
                          className={`text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full w-28 text-center border-2 transition-all active:scale-95 ${
                            item.available
                              ? "bg-white text-stone-700 border-stone-300 hover:bg-red-600 hover:text-white hover:border-red-600"
                              : "bg-red-600 text-white border-red-600 hover:bg-red-700"
                          }`}
                        >
                          {item.available ? "Mark Sold Out" : "Sold Out · tap"}
                        </button>
                        <button
                          onClick={() => setEditingId(item.id)}
                          data-testid={`admin-edit-${item.id}`}
                          className="text-stone-400 hover:text-brand-red p-1"
                          aria-label={`Edit ${item.name}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => del(item)}
                          data-testid={`admin-delete-${item.id}`}
                          className="text-stone-400 hover:text-brand-red p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
    </div>
  );
}

function AllowedExtrasPicker({
  addOns,
  value,
  onChange,
  overrides,
  onOverridesChange,
  testIdPrefix,
}) {
  // value === null => all allowed (legacy default). Toggling any checkbox
  // materializes the list explicitly. Selecting every add-on collapses back
  // to null so the item stays forward-compatible if new add-ons are added.
  const allIds = addOns.map((a) => a.id);
  const selected = value === null || value === undefined ? new Set(allIds) : new Set(value);
  const allChecked = value === null || value === undefined;
  const overrideMap = overrides || {};

  const toggle = (id) => {
    // Materialize the current selection then flip the toggled id.
    const next = new Set(allChecked ? allIds : value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // If back to "all", store as null so future add-ons stay allowed by default.
    if (next.size === allIds.length && allIds.every((i) => next.has(i))) {
      onChange(null);
    } else {
      onChange(Array.from(next));
    }
    // Also drop any override for a de-selected add-on (keeps the map tidy).
    if (!next.has(id) && overrideMap[id] !== undefined) {
      const { [id]: _, ...rest } = overrideMap;
      onOverridesChange(Object.keys(rest).length === 0 ? null : rest);
    }
  };

  const selectAll = () => onChange(null);
  const clearAll = () => {
    onChange([]);
    onOverridesChange(null);
  };

  const setOverride = (id, raw) => {
    const trimmed = String(raw).trim();
    if (trimmed === "") {
      // Clear override — falls back to global add-on price.
      const { [id]: _, ...rest } = overrideMap;
      onOverridesChange(Object.keys(rest).length === 0 ? null : rest);
      return;
    }
    const num = Number(trimmed);
    if (Number.isNaN(num) || num < 0) return;
    onOverridesChange({ ...overrideMap, [id]: Math.round(num * 100) / 100 });
  };

  if (addOns.length === 0) {
    return (
      <div className="text-xs text-stone-500 italic">
        No Add-Ons defined yet. Create Add-Ons in this menu first to restrict them per item.
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-stone-200 bg-white p-3"
      data-testid={`${testIdPrefix}-allowed-extras`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-600 font-black">
          Allowed Add-Ons & Per-Item Price
        </div>
        <div className="flex gap-2 text-[10px] uppercase tracking-widest font-bold">
          <button
            type="button"
            onClick={selectAll}
            className="text-emerald-700 hover:underline"
            data-testid={`${testIdPrefix}-allowed-extras-all`}
          >
            All
          </button>
          <span className="text-stone-300">·</span>
          <button
            type="button"
            onClick={clearAll}
            className="text-brand-red hover:underline"
            data-testid={`${testIdPrefix}-allowed-extras-none`}
          >
            None
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {addOns.map((a) => {
          const on = selected.has(a.id);
          const override = overrideMap[a.id];
          const hasOverride = override !== undefined && override !== null;
          return (
            <div
              key={a.id}
              className="flex items-center gap-2 text-xs text-stone-700"
              data-testid={`${testIdPrefix}-allowed-extra-${a.id}`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(a.id)}
                className="w-4 h-4 accent-brand-red shrink-0"
              />
              <span className="flex-1 truncate">{a.name}</span>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-stone-400">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  disabled={!on}
                  value={hasOverride ? String(override) : ""}
                  placeholder={a.price.toFixed(2)}
                  onChange={(e) => setOverride(a.id, e.target.value)}
                  data-testid={`${testIdPrefix}-extra-price-${a.id}`}
                  className={`w-16 px-1.5 py-0.5 rounded border text-right text-xs outline-none ${
                    hasOverride
                      ? "border-brand-red bg-red-50 font-bold text-brand-red"
                      : "border-stone-300 bg-white text-stone-500 focus:border-brand-red disabled:bg-stone-50 disabled:cursor-not-allowed"
                  }`}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-stone-500 space-y-0.5">
        <div>
          {allChecked
            ? "All add-ons will show for this item."
            : `${selected.size} of ${addOns.length} add-ons will show for this item.`}
        </div>
        <div>
          Leave a price blank to use the add-on&apos;s default. A red/bold value is
          a per-item override.
        </div>
      </div>
    </div>
  );
}

/**
 * ItemPhotoField — square photo picker used inside AddItemForm and
 * EditItemForm. Handles upload, preview, and clear. Items without a
 * saved image_url stay hidden from the customer menu.
 */
function ItemPhotoField({ token, value, onChange, testIdPrefix }) {
  return (
    <div
      className="rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 p-3"
      data-testid={`${testIdPrefix}-photo-field`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0">
          {value ? (
            <img
              src={value}
              alt="Item photo"
              data-testid={`${testIdPrefix}-photo-preview`}
              className="w-24 h-24 rounded-lg object-cover border border-stone-200 bg-white"
              onError={(e) => {
                e.currentTarget.style.opacity = "0.3";
              }}
            />
          ) : (
            <div className="w-24 h-24 rounded-lg border-2 border-dashed border-stone-300 bg-white text-[10px] uppercase tracking-widest font-black text-stone-400 flex items-center justify-center text-center leading-tight">
              No<br />photo
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-stone-600">
              Photo (optional)
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5 leading-snug">
              JPG, PNG, WebP — max 8 MB. Items look best with a photo, but
              they'll still show on the menu without one.
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <MediaUploadButton
              token={token}
              testId={`${testIdPrefix}-photo-upload`}
              kind="image"
              label={value ? "Replace photo" : "Upload photo"}
              onUploaded={(url) => onChange(url)}
            />
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                data-testid={`${testIdPrefix}-photo-clear`}
                className="text-[11px] uppercase tracking-widest font-bold text-brand-red hover:underline"
              >
                Remove photo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AddItemForm({ token, addOns, onClose, onSaved }) {
  const CATEGORIES = useMenuCategories();
  const FRIES_DEFAULT_CATEGORIES = new Set([
    "Burgers",
    "Sandwiches",
    "Wraps",
    "Vegetarian",
  ]);
  const initialCategory = CATEGORIES[0];
  const [form, setForm] = useState({
    category: initialCategory,
    name: "",
    description: "",
    price: "",
    image_url: "",
    allowed_extras: null,
    extras_price_overrides: null,
    includes_fries: FRIES_DEFAULT_CATEGORIES.has(initialCategory),
    includes_fries_touched: false,
    is_new: true,
    lto_active: false,
    lto_ends_at: "",
    lto_price: "",
  });
  const [saving, setSaving] = useState(false);

  const needsPhoto =
    form.category !== "Drinks" && form.category !== "Add-Ons";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi(token).post("/menu", {
        category: form.category,
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        available: true,
        image_url: form.image_url || null,
        allowed_extras: form.category === "Add-Ons" ? null : form.allowed_extras,
        extras_price_overrides:
          form.category === "Add-Ons" ? null : form.extras_price_overrides,
        includes_fries: form.includes_fries,
        is_new: form.is_new,
        lto_active: form.lto_active,
        lto_ends_at: form.lto_active && form.lto_ends_at ? form.lto_ends_at : null,
        lto_price:
          form.lto_active && form.lto_price
            ? parseFloat(form.lto_price)
            : null,
      });
      toast.success("Item added");
      onSaved();
    } catch {
      toast.error("Failed to add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-stone-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-6 gap-3"
      data-testid="admin-add-item-form"
    >
      <select
        value={form.category}
        onChange={(e) => {
          const nextCat = e.target.value;
          setForm((prev) => ({
            ...prev,
            category: nextCat,
            // Auto-toggle "fries included" to match the new category the first
            // time the owner picks one — but respect any manual override.
            includes_fries: prev.includes_fries_touched
              ? prev.includes_fries
              : FRIES_DEFAULT_CATEGORIES.has(nextCat),
          }));
        }}
        className="md:col-span-1 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
        data-testid="admin-form-category"
      >
        {CATEGORIES.map((c) => (
          <option key={c}>{c}</option>
        ))}
      </select>
      <input
        required
        placeholder="Name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="md:col-span-2 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
        data-testid="admin-form-name"
      />
      <input
        placeholder="Description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        className="md:col-span-2 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
        data-testid="admin-form-description"
      />
      <input
        required
        type="number"
        min="0"
        step="0.01"
        placeholder="Price"
        value={form.price}
        onChange={(e) => setForm({ ...form, price: e.target.value })}
        className="md:col-span-1 px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
        data-testid="admin-form-price"
      />
      {needsPhoto && (
        <div className="md:col-span-6">
          <ItemPhotoField
            token={token}
            value={form.image_url}
            onChange={(v) => setForm({ ...form, image_url: v })}
            testIdPrefix="admin-add-form"
          />
        </div>
      )}
      {form.category !== "Add-Ons" && (
        <div className="md:col-span-6">
          <AllowedExtrasPicker
            addOns={addOns}
            value={form.allowed_extras}
            onChange={(v) => setForm({ ...form, allowed_extras: v })}
            overrides={form.extras_price_overrides}
            onOverridesChange={(v) =>
              setForm({ ...form, extras_price_overrides: v })
            }
            testIdPrefix="admin-add-form"
          />
        </div>
      )}
      {form.category !== "Drinks" && form.category !== "Add-Ons" && (
        <label
          className="md:col-span-6 flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 cursor-pointer select-none"
          data-testid="admin-add-form-includes-fries-label"
        >
          <input
            type="checkbox"
            checked={form.includes_fries}
            onChange={(e) =>
              setForm({
                ...form,
                includes_fries: e.target.checked,
                includes_fries_touched: true,
              })
            }
            className="w-4 h-4 accent-brand-red"
            data-testid="admin-add-form-includes-fries"
          />
          <span className="text-sm text-stone-700">
            <span className="font-bold">🍟 Fries included</span>{" "}
            <span className="text-stone-500">
              — show the "Fries included" tag on the customer menu card.
            </span>
          </span>
        </label>
      )}
      {form.category !== "Add-Ons" && (
        <label
          className="md:col-span-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 cursor-pointer select-none"
          data-testid="admin-add-form-isnew-label"
        >
          <input
            type="checkbox"
            checked={form.is_new}
            onChange={(e) => setForm({ ...form, is_new: e.target.checked })}
            className="w-4 h-4 accent-emerald-600"
            data-testid="admin-add-form-isnew"
          />
          <span className="text-sm text-stone-700">
            <span className="font-bold">✨ Show "NEW" badge</span>{" "}
            <span className="text-stone-500">
              — pins a green NEW pill on the customer card until you untick it.
            </span>
          </span>
        </label>
      )}
      {form.category !== "Add-Ons" && (
        <div
          className="md:col-span-6 bg-red-50 border border-red-200 rounded-lg px-3 py-2 space-y-2"
          data-testid="admin-add-form-lto-block"
        >
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.lto_active}
              onChange={(e) =>
                setForm({ ...form, lto_active: e.target.checked })
              }
              className="w-4 h-4 accent-brand-red"
              data-testid="admin-add-form-lto-active"
            />
            <span className="text-sm text-stone-700">
              <span className="font-bold">🔥 Limited-time offer</span>{" "}
              <span className="text-stone-500">
                — pins a red LIMITED ribbon with a countdown, optionally at a promo price.
              </span>
            </span>
          </label>
          {form.lto_active && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
              <label className="flex flex-col gap-1 text-[11px] uppercase tracking-widest text-stone-600 font-bold">
                Ends at
                <input
                  type="datetime-local"
                  value={form.lto_ends_at}
                  onChange={(e) =>
                    setForm({ ...form, lto_ends_at: e.target.value })
                  }
                  data-testid="admin-add-form-lto-ends"
                  className="px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] uppercase tracking-widest text-stone-600 font-bold">
                Promo price ($) — optional
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.lto_price}
                  onChange={(e) =>
                    setForm({ ...form, lto_price: e.target.value })
                  }
                  placeholder="e.g. 9.99"
                  data-testid="admin-add-form-lto-price"
                  className="px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red font-mono"
                />
              </label>
            </div>
          )}
        </div>
      )}
      <div className="md:col-span-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold text-stone-600 hover:bg-stone-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-form-submit"
          className="bg-brand-red text-white px-5 py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-red-800 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Item"}
        </button>
      </div>
    </form>
  );
}


function EditItemForm({ token, item, addOns, onClose, onSaved }) {
  const CATEGORIES = useMenuCategories();
  const FRIES_DEFAULT_CATEGORIES = new Set([
    "Burgers",
    "Sandwiches",
    "Wraps",
    "Vegetarian",
  ]);
  const [form, setForm] = useState({
    category: item.category,
    name: item.name,
    description: item.description || "",
    price: String(item.price),
    image_url: item.image_url || "",
    allowed_extras:
      item.allowed_extras === undefined ? null : item.allowed_extras,
    extras_price_overrides:
      item.extras_price_overrides === undefined
        ? null
        : item.extras_price_overrides,
    includes_fries: !!item.includes_fries,
    is_new: !!item.is_new,
    lto_active: !!item.lto_active,
    lto_ends_at: isoToLocalInput(item.lto_ends_at),
    lto_price:
      item.lto_price != null && item.lto_price !== undefined
        ? String(item.lto_price)
        : "",
  });
  const [saving, setSaving] = useState(false);

  const needsPhoto =
    form.category !== "Drinks" && form.category !== "Add-Ons";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi(token).put(`/menu/${item.id}`, {
        category: form.category,
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        image_url: form.image_url || null,
        allowed_extras:
          form.category === "Add-Ons" ? null : form.allowed_extras,
        extras_price_overrides:
          form.category === "Add-Ons" ? null : form.extras_price_overrides,
        includes_fries: form.includes_fries,
        is_new: form.is_new,
        lto_active: form.lto_active,
        lto_ends_at:
          form.lto_active && form.lto_ends_at
            ? localInputToIso(form.lto_ends_at)
            : null,
        lto_price:
          form.lto_active && form.lto_price
            ? parseFloat(form.lto_price)
            : null,
      });
      toast.success(
        !needsPhoto || form.image_url
          ? "Item updated"
          : "Item updated — hidden from customers until a photo is added"
      );
      onSaved();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`admin-edit-form-${item.id}`}
      className="bg-brand-yellow/10 border-l-4 border-brand-red px-4 py-3 grid grid-cols-1 md:grid-cols-6 gap-3"
    >
      <select
        value={form.category}
        onChange={(e) => setForm({ ...form, category: e.target.value })}
        className="md:col-span-1 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
        data-testid={`admin-edit-category-${item.id}`}
      >
        {CATEGORIES.map((c) => (
          <option key={c}>{c}</option>
        ))}
      </select>
      <input
        required
        placeholder="Name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="md:col-span-2 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
        data-testid={`admin-edit-name-${item.id}`}
      />
      <input
        placeholder="Description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        className="md:col-span-2 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
        data-testid={`admin-edit-description-${item.id}`}
      />
      <input
        required
        type="number"
        min="0"
        step="0.01"
        placeholder="Price"
        value={form.price}
        onChange={(e) => setForm({ ...form, price: e.target.value })}
        className="md:col-span-1 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
        data-testid={`admin-edit-price-${item.id}`}
      />
      {needsPhoto && (
        <div className="md:col-span-6">
          <ItemPhotoField
            token={token}
            value={form.image_url}
            onChange={(v) => setForm({ ...form, image_url: v })}
            testIdPrefix={`admin-edit-${item.id}`}
          />
        </div>
      )}
      {form.category !== "Add-Ons" && (
        <div className="md:col-span-6">
          <AllowedExtrasPicker
            addOns={addOns}
            value={form.allowed_extras}
            onChange={(v) => setForm({ ...form, allowed_extras: v })}
            overrides={form.extras_price_overrides}
            onOverridesChange={(v) =>
              setForm({ ...form, extras_price_overrides: v })
            }
            testIdPrefix={`admin-edit-${item.id}`}
          />
        </div>
      )}
      {form.category !== "Drinks" && form.category !== "Add-Ons" && (
        <label
          className="md:col-span-6 flex items-center gap-3 bg-white border border-stone-200 rounded-lg px-3 py-2 cursor-pointer select-none"
          data-testid={`admin-edit-includes-fries-label-${item.id}`}
        >
          <input
            type="checkbox"
            checked={form.includes_fries}
            onChange={(e) =>
              setForm({ ...form, includes_fries: e.target.checked })
            }
            className="w-4 h-4 accent-brand-red"
            data-testid={`admin-edit-includes-fries-${item.id}`}
          />
          <span className="text-sm text-stone-700">
            <span className="font-bold">🍟 Fries included</span>{" "}
            <span className="text-stone-500">
              — show the "Fries included" tag on the customer menu card.
            </span>
          </span>
        </label>
      )}
      {form.category !== "Add-Ons" && (
        <label
          className="md:col-span-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 cursor-pointer select-none"
          data-testid={`admin-edit-isnew-label-${item.id}`}
        >
          <input
            type="checkbox"
            checked={form.is_new}
            onChange={(e) => setForm({ ...form, is_new: e.target.checked })}
            className="w-4 h-4 accent-emerald-600"
            data-testid={`admin-edit-isnew-${item.id}`}
          />
          <span className="text-sm text-stone-700">
            <span className="font-bold">✨ Show "NEW" badge</span>{" "}
            <span className="text-stone-500">
              — pins a green NEW pill on the customer card until unticked.
            </span>
          </span>
        </label>
      )}
      {form.category !== "Add-Ons" && (
        <div
          className="md:col-span-6 bg-red-50 border border-red-200 rounded-lg px-3 py-2 space-y-2"
          data-testid={`admin-edit-lto-block-${item.id}`}
        >
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.lto_active}
              onChange={(e) =>
                setForm({ ...form, lto_active: e.target.checked })
              }
              className="w-4 h-4 accent-brand-red"
              data-testid={`admin-edit-lto-active-${item.id}`}
            />
            <span className="text-sm text-stone-700">
              <span className="font-bold">🔥 Limited-time offer</span>{" "}
              <span className="text-stone-500">
                — pins a red LIMITED ribbon with a countdown, optionally at a promo price.
              </span>
            </span>
          </label>
          {form.lto_active && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
              <label className="flex flex-col gap-1 text-[11px] uppercase tracking-widest text-stone-600 font-bold">
                Ends at
                <input
                  type="datetime-local"
                  value={form.lto_ends_at}
                  onChange={(e) =>
                    setForm({ ...form, lto_ends_at: e.target.value })
                  }
                  data-testid={`admin-edit-lto-ends-${item.id}`}
                  className="px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] uppercase tracking-widest text-stone-600 font-bold">
                Promo price ($) — optional
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.lto_price}
                  onChange={(e) =>
                    setForm({ ...form, lto_price: e.target.value })
                  }
                  placeholder="e.g. 9.99"
                  data-testid={`admin-edit-lto-price-${item.id}`}
                  className="px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red font-mono"
                />
              </label>
            </div>
          )}
        </div>
      )}
      <div className="md:col-span-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold text-stone-600 hover:bg-stone-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          data-testid={`admin-edit-save-${item.id}`}
          className="bg-brand-red text-white px-5 py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-red-800 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
}


// ---------------- Site Content Tab ----------------

/**
 * Convert an ISO 8601 string (UTC or offset) to the value expected by a
 * `<input type="datetime-local">` — i.e. "YYYY-MM-DDTHH:mm" in the
 * browser's local timezone. Empty / invalid strings become "".
 */
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/**
 * Convert a datetime-local input value ("YYYY-MM-DDTHH:mm", browser
 * local tz) back into a UTC ISO string suitable for `site_settings`.
 * Empty inputs become "".
 */
function localInputToIso(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
}

/**
 * MediaUploadButton — uploads a short mp4/webm clip OR an image to the
 * matching admin upload endpoint and returns the resulting public URL
 * through onUploaded so the parent form can save it back. Shows an inline
 * progress state and surfaces backend errors as toasts.
 */

/**
 * ReoptimizeImagesButton — one-shot re-encode of every already-uploaded
 * menu photo through the new resize/quality pipeline. Reports back the
 * bytes saved so the owner sees a concrete win.
 */
function ReoptimizeImagesButton({ token }) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (
      !window.confirm(
        "Re-shrink every menu photo already uploaded? This may take a minute."
      )
    )
      return;
    setBusy(true);
    try {
      const { data } = await adminApi(token).post(
        "/admin/reoptimize-menu-images"
      );
      const savedMB = ((data.saved_bytes || 0) / 1024 / 1024).toFixed(2);
      if (data.reoptimised > 0) {
        toast.success(
          `Shrunk ${data.reoptimised} photo${
            data.reoptimised === 1 ? "" : "s"
          } — saved ${savedMB} MB`
        );
      } else {
        toast.message("No photos needed shrinking — you're already optimised!");
      }
      if (data.failed && data.failed.length) {
        toast.error(`${data.failed.length} photo(s) couldn't be processed`);
      }
    } catch {
      toast.error("Re-optimize failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid="admin-reoptimize-images-btn"
      title="Re-encode existing menu photos smaller & faster"
      className="bg-stone-800 hover:bg-stone-700 text-white text-xs uppercase tracking-widest font-bold px-3 py-2 rounded-full flex items-center gap-1 disabled:opacity-50 disabled:cursor-wait"
    >
      <Sparkles className="w-4 h-4" />
      {busy ? "Optimizing..." : "Re-optimize photos"}
    </button>
  );
}


function MediaUploadButton({
  token,
  testId,
  onUploaded,
  kind = "video", // "video" | "image"
  label = "Upload from device",
}) {
  const [uploading, setUploading] = useState(false);
  const isImage = kind === "image";
  const endpoint = isImage ? "/admin/upload-image" : "/admin/upload-video";
  const accept = isImage
    ? "image/jpeg,image/png,image/webp,image/gif"
    : "video/mp4,video/webm,video/quicktime";
  const maxBytes = isImage ? 8 * 1024 * 1024 : 25 * 1024 * 1024;

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > maxBytes) {
      toast.error(
        `${isImage ? "Image" : "Video"} too large — max ${maxBytes / (1024 * 1024)} MB`
      );
      return;
    }
    const wantedPrefix = isImage ? "image/" : "video/";
    if (!file.type.startsWith(wantedPrefix)) {
      toast.error(`Please pick ${isImage ? "an image" : "a video"} file`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi(token).post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
      toast.success(
        `${isImage ? "Photo" : "Video"} uploaded — remember to save`
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          `Upload failed. Try a smaller ${isImage ? "image" : "mp4"}.`
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <label
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold cursor-pointer transition-colors ${
        uploading
          ? "bg-stone-200 text-stone-500 cursor-wait"
          : "bg-stone-900 text-white hover:bg-stone-700"
      }`}
      data-testid={testId}
    >
      <Upload className="w-3.5 h-3.5" />
      {uploading ? "Uploading..." : label}
      <input
        type="file"
        accept={accept}
        className="hidden"
        disabled={uploading}
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
    </label>
  );
}

// Back-compat alias used by the banner-video field renderer.
const VideoUploadButton = (props) => (
  <MediaUploadButton {...props} kind="video" />
);


const PROTECTED_CATEGORIES = new Set(["Drinks", "Add-Ons"]);

/**
 * CategoriesEditor — inline manager for `menu_categories`. Drag-free list
 * with up/down reorder arrows, rename inline, remove non-protected items,
 * a colour picker per row (which persists into `menu_category_colors` on
 * the form), and an "Add category" input. Drinks + Add-Ons are locked
 * (the customer app hard-codes special rendering for them).
 */
function CategoriesEditor({ value, onChange, form, setForm, testIdPrefix }) {
  const [draft, setDraft] = useState("");
  const cats = Array.isArray(value) ? value : [];
  const colors = form?.menu_category_colors || {};

  const setColor = (name, hex) => {
    setForm({
      ...form,
      menu_category_colors: { ...(form.menu_category_colors || {}), [name]: hex },
    });
  };
  const clearColor = (name) => {
    const next = { ...(form.menu_category_colors || {}) };
    delete next[name];
    setForm({ ...form, menu_category_colors: next });
  };

  const move = (idx, dir) => {
    const next = [...cats];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  const rename = (idx, name) => {
    const next = [...cats];
    next[idx] = name;
    onChange(next);
  };

  const remove = (idx) => {
    if (PROTECTED_CATEGORIES.has(cats[idx])) {
      toast.error(`"${cats[idx]}" can't be removed — the app depends on it`);
      return;
    }
    onChange(cats.filter((_, i) => i !== idx));
  };

  const add = () => {
    const name = draft.trim();
    if (!name) return;
    if (cats.some((c) => c.toLowerCase() === name.toLowerCase())) {
      toast.error(`"${name}" already exists`);
      return;
    }
    onChange([...cats, name]);
    setDraft("");
  };

  return (
    <div className="mt-2 space-y-2" data-testid={testIdPrefix}>
      <ul className="space-y-1.5">
        {cats.map((c, i) => {
          const locked = PROTECTED_CATEGORIES.has(c);
          return (
            <li
              key={i}
              className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-2 py-1.5"
              data-testid={`${testIdPrefix}-item-${i}`}
            >
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  aria-label="Move up"
                  className="w-5 h-4 text-stone-400 hover:text-brand-red disabled:opacity-30"
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === cats.length - 1}
                  aria-label="Move down"
                  className="w-5 h-4 text-stone-400 hover:text-brand-red disabled:opacity-30"
                >
                  ▼
                </button>
              </div>
              <input
                value={c}
                onChange={(e) => rename(i, e.target.value)}
                disabled={locked}
                data-testid={`${testIdPrefix}-name-${i}`}
                className={`flex-1 px-2 py-1 rounded border text-sm outline-none ${
                  locked
                    ? "bg-stone-100 border-stone-200 text-stone-500 cursor-not-allowed"
                    : "bg-white border-stone-300 focus:border-brand-red"
                }`}
              />
              <div className="flex items-center gap-1">
                <input
                  type="color"
                  value={colors[c] || "#c0392b"}
                  onChange={(e) => setColor(c, e.target.value)}
                  data-testid={`${testIdPrefix}-color-${i}`}
                  title={colors[c] ? `Custom: ${colors[c]}` : "Auto colour — click to set custom"}
                  className="w-8 h-8 rounded border border-stone-300 bg-white cursor-pointer"
                />
                {colors[c] && (
                  <button
                    type="button"
                    onClick={() => clearColor(c)}
                    data-testid={`${testIdPrefix}-color-reset-${i}`}
                    title="Reset to auto colour"
                    className="text-[10px] uppercase tracking-widest font-bold text-stone-400 hover:text-brand-red px-1"
                  >
                    Auto
                  </button>
                )}
              </div>
              {locked ? (
                <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold px-1">
                  Locked
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => remove(i)}
                  data-testid={`${testIdPrefix}-remove-${i}`}
                  className="text-stone-400 hover:text-brand-red p-1"
                  aria-label={`Remove ${c}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="New category name (e.g. Desserts)"
          data-testid={`${testIdPrefix}-input`}
          className="flex-1 px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          data-testid={`${testIdPrefix}-add-btn`}
          className="px-3 py-2 rounded-lg bg-brand-red text-white text-[11px] uppercase tracking-widest font-bold hover:bg-red-800 disabled:opacity-40 flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" />
          Add
        </button>
      </div>
    </div>
  );
}


const SITE_FIELDS = [
  { group: "Brand & Hero", fields: [
    { key: "brand_name",    label: "Brand name (hero title)",          type: "input" },
    { key: "hero_welcome",  label: "Hero eyebrow ('Welcome to')",       type: "input" },
    { key: "hero_tagline",  label: "Hero tagline (under title)",        type: "textarea" },
    { key: "hero_cta_label",label: "Hero CTA button label",             type: "input" },
  ]},
  { group: "Owner Attribution", fields: [
    { key: "owner_line",    label: "Attribution line",                  type: "input" },
    { key: "owner_names",   label: "Owner names (large cursive)",       type: "input" },
  ]},
  { group: "Truck Info", fields: [
    { key: "phone",         label: "Phone number",                      type: "input" },
    { key: "hours",         label: "Hours (info strip)",                type: "input" },
  ]},
  { group: "Today's Truck Location", fields: [
    { key: "truck_location_active",  label: "Show location card on homepage",         type: "toggle" },
    { key: "truck_location_name",    label: "Spot name (e.g. Downtown Farmers Market)", type: "input" },
    { key: "truck_location_address", label: "Address (opens Google Maps for customers)", type: "input" },
    { key: "truck_location_note",    label: "Short note (optional)",                  type: "input" },
  ]},
  { group: "Tax Rates", fields: [
    { key: "sales_tax_rate", label: "Sales tax %", type: "percent" },
  ]},
  { group: "Clock-Out Reminder", fields: [
    { key: "clock_out_reminder_enabled", label: "Email me when someone is still clocked in past the cutoff", type: "toggle" },
    { key: "clock_out_reminder_time",    label: "Cutoff time (Central) — checked every 5 minutes, one heads-up per shift, nothing is changed automatically", type: "time" },
  ]},
  { group: "Ordering On/Off", fields: [
    { key: "orders_closed_message", label: 'Message shown when ordering is paused (tap the "Ordering" toggle in the top-right to switch it)', type: "textarea" },
  ]},
  { group: "Free Drinks", fields: [
    { key: "drinks_free_enabled", label: "Give one FREE drink with every online order", type: "toggle" },
    { key: "drinks_free_headline", label: "Free-drinks banner headline (shown above the Drinks section)" },
    { key: "drinks_free_subtitle", label: "Free-drinks banner subtitle (optional)" },
    { key: "drinks_free_window_enabled", label: "Only free during a specific date/time window (leave off for 'always free while enabled')", type: "toggle" },
    { key: "drinks_free_starts_at", label: "Free-drinks START date & time (only used when the window above is ON)", type: "datetime" },
    { key: "drinks_free_ends_at",   label: "Free-drinks END date & time (only used when the window above is ON)", type: "datetime" },
  ]},
  { group: "About Us", fields: [
    { key: "about_headline", label: "About Us — hand-written hero headline" },
    { key: "about_founded_year", label: "Founded year (shown as 'Serving hearts since ____')" },
    { key: "about_story", label: "Our Story — full paragraph on /about", type: "textarea" },
    { key: "about_mission", label: "Our Mission — full paragraph on /about", type: "textarea" },
  ]},
  { group: "Menu Categories", fields: [
    { key: "menu_categories", label: "Categories shown on the customer menu. 'Drinks' and 'Add-Ons' are protected — the app needs them to work.", type: "categories" },
  ]},
  { group: "Homepage Banner Videos", fields: [
    { key: "open_banner_video_url",   label: 'Video shown when ordering is OPEN (looping, muted). Paste a direct .mp4 / .webm URL.', type: "video" },
    { key: "closed_banner_video_url", label: "Video shown when ordering is PAUSED (looping, muted). Paste a direct .mp4 / .webm URL.", type: "video" },
  ]},
  { group: "Rewards Copy", fields: [
    { key: "rewards_signup_headline", label: "Rewards signup headline", type: "input" },
    { key: "rewards_signup_tagline",  label: "Rewards signup tagline",  type: "input" },
    { key: "loyalty_card_headline",   label: "Home loyalty card headline", type: "input" },
    { key: "loyalty_card_subtext",    label: "Home loyalty card subtext",  type: "input" },
  ]},
  { group: "Checkout & Legal", fields: [
    { key: "checkout_footer", label: "Checkout footer message", type: "textarea" },
    { key: "advisory",        label: "Food safety advisory",    type: "textarea" },
  ]},
];

// Every key the SiteTab form owns (SITE_FIELDS + the cards that share `form`).
const FORM_KEYS = [
  ...SITE_FIELDS.flatMap((g) => g.fields.map((f) => f.key)),
  "featured_items",
  "pos_grid_columns",
  "kds_online_bg",
  "kds_online_text",
  "kds_pos_bg",
  "kds_pos_text",
];

// ---------------- Chat Tab ----------------

/**
 * ChatTab — inbox of every live customer thread. Two-pane layout:
 * left = thread list with unread badges + last message preview,
 * right = the selected conversation. Polls every 5 s.
 */
export function ChatTab({ token }) {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get("/admin/chat/threads");
      setThreads(data.threads || []);
    } catch {
      /* transient */
    }
  }, [token]);

  const loadThread = useCallback(
    async (id) => {
      try {
        const { data } = await adminApi(token).get(
          `/admin/chat/threads/${id}`
        );
        setMsgs(data.messages || []);
      } catch {
        /* transient */
      }
    },
    [token]
  );

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 5000);
    return () => clearInterval(t);
  }, [loadThreads]);

  useEffect(() => {
    if (!selected) return;
    loadThread(selected);
    const t = setInterval(() => loadThread(selected), 4000);
    return () => clearInterval(t);
  }, [selected, loadThread]);

  useEffect(() => {
    if (selected) {
      adminApi(token)
        .post(`/admin/chat/threads/${selected}/mark-read`)
        .catch(() => {});
      loadThreads();
    }
  }, [selected, token, loadThreads]);

  useEffect(() => {
    setTimeout(
      () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
      50
    );
  }, [msgs]);

  const send = async () => {
    if (!selected || !draft.trim()) return;
    setSending(true);
    try {
      await adminApi(token).post(`/admin/chat/threads/${selected}/messages`, {
        text: draft.trim(),
      });
      setDraft("");
      await loadThread(selected);
      await loadThreads();
    } catch {
      toast.error("Couldn't send");
    } finally {
      setSending(false);
    }
  };

  const removeThread = async (id) => {
    if (
      !window.confirm(
        "Delete this whole conversation? This can't be undone."
      )
    )
      return;
    try {
      await adminApi(token).delete(`/admin/chat/threads/${id}`);
      if (selected === id) {
        setSelected(null);
        setMsgs([]);
      }
      await loadThreads();
      toast.success("Conversation deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const closeThread = async (id) => {
    try {
      await adminApi(token).post(`/admin/chat/threads/${id}/close`);
      await loadThreads();
      toast.success("Chat closed — the customer was notified");
    } catch {
      toast.error("Couldn't close chat");
    }
  };

  const activeThread = threads.find((t) => t.id === selected);
  const fmtTime = (iso) =>
    iso
      ? new Date(iso).toLocaleString([], {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : "";

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 min-h-[500px]"
      data-testid="admin-chat-tab"
    >
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden flex flex-col max-h-[70vh]">
        <div className="chalkboard-band px-3 py-2 text-brand-yellow font-display text-lg">
          Conversations
        </div>
        {threads.length === 0 ? (
          <div className="text-center text-stone-400 text-sm py-8 px-4">
            No chats yet. Customers can start one from their Order Status page.
          </div>
        ) : (
          <ul className="divide-y divide-stone-100 overflow-y-auto">
            {threads.map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => setSelected(t.id)}
                  data-testid={`admin-chat-thread-${t.id}`}
                  className={`w-full text-left px-3 py-3 hover:bg-stone-50 transition-colors flex items-start gap-2 ${
                    selected === t.id ? "bg-stone-100" : ""
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-stone-800 truncate">
                        {t.name}
                      </span>
                      {t.unread_admin_count > 0 && (
                        <span className="bg-brand-red text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center shrink-0">
                          {t.unread_admin_count > 9 ? "9+" : t.unread_admin_count}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-stone-500 truncate">
                      {t.phone}
                      {t.order_number ? ` · #${t.order_number}` : ""}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-0.5">
                      {fmtTime(t.last_message_at)}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-xl border border-stone-200 flex flex-col max-h-[70vh]">
        {!activeThread ? (
          <div className="flex-1 flex items-center justify-center text-stone-400 text-sm p-8">
            Select a conversation from the left
          </div>
        ) : (
          <>
            <div className="chalkboard-band px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-brand-yellow font-display text-lg leading-tight">
                  {activeThread.name}
                </div>
                <div className="text-[11px] text-white/80 font-mono">
                  {activeThread.phone}
                  {activeThread.order_number
                    ? ` · Order #${activeThread.order_number}`
                    : ""}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {activeThread.closed && (
                  <span className="text-[9px] uppercase tracking-widest font-black bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full" data-testid="admin-chat-closed-tag">
                    Closed
                  </span>
                )}
                {!activeThread.closed && (
                  <button
                    onClick={() => closeThread(activeThread.id)}
                    data-testid="admin-chat-close-thread"
                    className="text-white/70 hover:text-white text-[11px] uppercase tracking-widest font-bold flex items-center gap-1"
                    title="Close this conversation (keeps the history)"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Close
                  </button>
                )}
                <button
                  onClick={() => removeThread(activeThread.id)}
                  data-testid="admin-chat-delete-thread"
                  className="text-white/70 hover:text-white text-[11px] uppercase tracking-widest font-bold flex items-center gap-1"
                  title="Delete this conversation"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
            <div
              className="flex-1 overflow-y-auto p-3 space-y-2 bg-stone-50"
              data-testid="admin-chat-msgs"
            >
              {msgs.map((m) => (
                <div
                  key={m.id}
                  className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-snug shadow-sm ${
                    m.sender === "admin"
                      ? "ml-auto bg-brand-red text-white rounded-br-sm"
                      : "mr-auto bg-white text-stone-800 border border-stone-200 rounded-bl-sm"
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">
                    {m.text}
                  </div>
                  <div
                    className={`text-[9px] uppercase tracking-widest mt-1 ${
                      m.sender === "admin" ? "text-white/70" : "text-stone-400"
                    }`}
                  >
                    {fmtTime(m.created_at)}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="p-2 border-t border-stone-200 flex gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Type a reply…"
                data-testid="admin-chat-reply-input"
                className="flex-1 px-3 py-2 rounded-full border border-stone-300 text-sm outline-none focus:border-brand-red"
              />
              <button
                onClick={send}
                disabled={sending || !draft.trim()}
                data-testid="admin-chat-send-btn"
                className="w-10 h-10 rounded-full bg-brand-red text-white flex items-center justify-center disabled:opacity-40 hover:bg-red-800 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- Reviews Tab ----------------

/**
 * ReviewsTab — every submitted review with owner reply input and permanent
 * delete. Deletes are hard (per owner request) and confirmed with a native
 * dialog to prevent accidental clicks.
 */
function ReviewsTab({ token }) {
  const [reviews, setReviews] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get("/admin/reviews");
      setReviews(data.reviews || []);
    } catch {
      toast.error("Couldn't load reviews");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const saveReply = async (id) => {
    const text = (drafts[id] || "").trim();
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await adminApi(token).put(`/admin/reviews/${id}`, { reply: text });
      setDrafts((d) => {
        const next = { ...d };
        delete next[id];
        return next;
      });
      await load();
      toast.success(text ? "Reply posted" : "Reply cleared");
    } catch {
      toast.error("Couldn't save reply");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const remove = async (id) => {
    if (
      !window.confirm("Delete this review? It moves to Recently deleted, where you can restore it.")
    )
      return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await adminApi(token).delete(`/admin/reviews/${id}`);
      await load();
      await loadTrash();
      toast.success("Review moved to Recently deleted");
    } catch {
      toast.error("Couldn't delete");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const [trash, setTrash] = useState([]);
  const loadTrash = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get("/admin/reviews/trash");
      setTrash(data);
    } catch {}
  }, [token]);
  useEffect(() => { loadTrash(); }, [loadTrash]);

  const restore = async (id) => {
    try {
      await adminApi(token).post(`/admin/reviews/${id}/restore`);
      await Promise.all([load(), loadTrash()]);
      toast.success("Review restored");
    } catch {
      toast.error("Couldn't restore");
    }
  };

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString([], {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";

  return (
    <div className="space-y-4" data-testid="admin-reviews-tab">
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="chalkboard-band px-4 py-2 flex items-center justify-between">
          <div className="text-brand-yellow font-display text-lg">
            Customer Reviews
          </div>
          <button
            onClick={load}
            className="text-white/80 hover:text-white text-xs uppercase tracking-widest font-bold flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
        {reviews.length === 0 ? (
          <div className="text-center text-stone-400 text-sm py-8">
            No reviews yet.
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {reviews.map((r) => {
              const currentReply =
                drafts[r.id] !== undefined ? drafts[r.id] : r.reply || "";
              const dirty =
                drafts[r.id] !== undefined && drafts[r.id] !== (r.reply || "");
              return (
                <li
                  key={r.id}
                  className="px-4 py-4 space-y-3"
                  data-testid={`admin-review-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-stone-800">
                          {r.name}
                        </span>
                        <span className="text-xs text-stone-400 font-mono">
                          {fmtDate(r.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i <= (r.rating || 0)
                                ? "text-brand-yellow fill-brand-yellow"
                                : "text-stone-300"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => remove(r.id)}
                      disabled={busy[r.id]}
                      data-testid={`admin-review-delete-${r.id}`}
                      className="text-brand-red hover:text-red-700 text-xs uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                  {r.text && (
                    <div className="text-sm text-stone-700 whitespace-pre-wrap">
                      {r.text}
                    </div>
                  )}
                  <div className="pl-4 border-l-4 border-brand-yellow bg-amber-50 rounded-r-lg p-3 space-y-2">
                    <div className="text-[10px] uppercase tracking-widest font-black text-brand-red flex items-center gap-1">
                      <Reply className="w-3 h-3" />
                      Your reply (public)
                    </div>
                    <textarea
                      rows={2}
                      value={currentReply}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [r.id]: e.target.value }))
                      }
                      placeholder="Thanks for the kind words! …"
                      data-testid={`admin-review-reply-input-${r.id}`}
                      className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red resize-none bg-white"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => saveReply(r.id)}
                        disabled={busy[r.id] || !dirty}
                        data-testid={`admin-review-reply-save-${r.id}`}
                        className="px-3 py-1.5 rounded-full bg-brand-red text-white text-[11px] uppercase tracking-widest font-bold hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {r.reply ? "Update reply" : "Post reply"}
                      </button>
                      {r.reply && (
                        <button
                          onClick={() =>
                            setDrafts((d) => ({ ...d, [r.id]: "" }))
                          }
                          className="text-[11px] uppercase tracking-widest font-bold text-stone-500 hover:text-brand-red"
                        >
                          Clear reply
                        </button>
                      )}
                      {r.replied_at && (
                        <span className="text-[10px] text-stone-400 ml-auto">
                          Replied {fmtDate(r.replied_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {trash.length > 0 && (
        <div className="bg-white rounded-xl border border-dashed border-stone-300 overflow-hidden" data-testid="admin-reviews-trash">
          <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-stone-500 font-bold border-b border-stone-100">
            Recently deleted ({trash.length}) — restore anything removed by mistake
          </div>
          <ul className="divide-y divide-stone-100">
            {trash.map((r) => (
              <li key={r.id} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`admin-review-trash-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-stone-700 text-sm">{r.name} · {"★".repeat(r.rating || 0)}</div>
                  <div className="text-xs text-stone-500 line-clamp-2">{r.text}</div>
                  <div className="text-[10px] text-stone-400 mt-0.5">deleted {fmtDate(r.deleted_at)}</div>
                </div>
                <button type="button" onClick={() => restore(r.id)} data-testid={`admin-review-restore-${r.id}`}
                  className="shrink-0 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border border-stone-800 text-stone-800 hover:bg-stone-800 hover:text-white">
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// ---------------- Kitchen Temperature Log Tab ----------------

const EMPTY_TEMP_ROW = { food_name: "", time: "", temp: "", initials: "" };
const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * TempLogTab — daily food-temperature check sheet. Owner picks a date,
 * adds rows (food name, time, temp, initials), saves, and can print / save
 * as PDF via the browser (window.print with a print-only stylesheet).
 * Past logs are listed on the left so the owner can reprint any day.
 */
const TEMP_LOG_DEFAULT_EMAIL = "blossomkitchenelkins@gmail.com";
const TEMP_LOG_EMAIL_KEY = "bk_temp_log_email_to";

function TempLogTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({
    date: todayISO(),
    entries: [{ ...EMPTY_TEMP_ROW }],
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get("/admin/temp-logs");
      setLogs(data.logs || []);
    } catch {
      toast.error("Couldn't load temp logs");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const openLog = (log) => {
    setSelectedId(log.id);
    setForm({
      date: log.date || todayISO(),
      entries: log.entries?.length ? log.entries : [{ ...EMPTY_TEMP_ROW }],
      notes: log.notes || "",
    });
  };

  const newLog = () => {
    setSelectedId(null);
    setForm({
      date: todayISO(),
      entries: [{ ...EMPTY_TEMP_ROW }],
      notes: "",
    });
  };

  const setRow = (idx, patch) =>
    setForm((f) => ({
      ...f,
      entries: f.entries.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));

  const addRow = () =>
    setForm((f) => ({ ...f, entries: [...f.entries, { ...EMPTY_TEMP_ROW }] }));

  const removeRow = (idx) =>
    setForm((f) => ({
      ...f,
      entries:
        f.entries.length > 1
          ? f.entries.filter((_, i) => i !== idx)
          : f.entries,
    }));

  const save = async () => {
    const entries = form.entries.filter(
      (r) => r.food_name || r.time || r.temp || r.initials
    );
    if (entries.length === 0) {
      toast.error("Add at least one row before saving");
      return;
    }
    setSaving(true);
    try {
      const payload = { date: form.date, entries, notes: form.notes };
      if (selectedId) {
        await adminApi(token).put(`/admin/temp-logs/${selectedId}`, payload);
        toast.success("Log updated");
      } else {
        const { data } = await adminApi(token).post("/admin/temp-logs", payload);
        setSelectedId(data.id);
        toast.success("Log saved");
      }
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    if (!window.confirm("Delete this temp log? This can't be undone.")) return;
    try {
      await adminApi(token).delete(`/admin/temp-logs/${selectedId}`);
      toast.success("Deleted");
      newLog();
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const printSheet = () => window.print();

  const [emailing, setEmailing] = useState(false);
  const [showEmailTo, setShowEmailTo] = useState(false);
  const [emailTo, setEmailTo] = useState(
    () => localStorage.getItem(TEMP_LOG_EMAIL_KEY) || TEMP_LOG_DEFAULT_EMAIL
  );

  const emailLog = async () => {
    const entries = form.entries.filter(
      (r) => r.food_name || r.time || r.temp || r.initials
    );
    if (entries.length === 0) {
      toast.error("Add at least one row before emailing");
      return;
    }
    setEmailing(true);
    try {
      const { data } = await adminApi(token).post("/admin/temp-logs/email", {
        date: form.date,
        entries,
        notes: form.notes,
        to: emailTo,
      });
      toast.success(`Temp log emailed to ${data.to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email failed — try again");
    } finally {
      setEmailing(false);
    }
  };

  const fmt = (iso) =>
    iso
      ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString(
          [],
          { month: "short", day: "numeric", year: "numeric" }
        )
      : "";

  const filledRows = form.entries.filter(
    (r) => r.food_name || r.time || r.temp || r.initials
  );

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .temp-log-print, .temp-log-print * { visibility: visible !important; }
          .temp-log-print {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            padding: 20px !important;
            background: white !important;
            color: black !important;
            display: block !important;
          }
        }
        .temp-log-print { display: none; }
      `}</style>

      <div
        className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4"
        data-testid="admin-temp-log-tab"
      >
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden flex flex-col max-h-[70vh]">
          <div className="chalkboard-band px-3 py-2 flex items-center justify-between">
            <div className="text-brand-yellow font-display text-lg">
              Past Logs
            </div>
            <button
              onClick={newLog}
              data-testid="temp-log-new-btn"
              className="text-white/80 hover:text-white text-[11px] uppercase tracking-widest font-bold flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" />
              New
            </button>
          </div>
          {logs.length === 0 ? (
            <div className="text-center text-stone-400 text-sm py-8 px-4">
              No logs yet. Start today's log on the right.
            </div>
          ) : (
            <ul className="divide-y divide-stone-100 overflow-y-auto">
              {logs.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => openLog(l)}
                    data-testid={`temp-log-open-${l.id}`}
                    className={`w-full text-left px-3 py-2 hover:bg-stone-50 ${
                      selectedId === l.id ? "bg-stone-100" : ""
                    }`}
                  >
                    <div className="font-bold text-stone-800">{fmt(l.date)}</div>
                    <div className="text-[11px] text-stone-500">
                      {l.entries.length} entr{l.entries.length === 1 ? "y" : "ies"}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="chalkboard-band px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-brand-yellow font-display text-xl leading-tight">
                Kitchen Temperature Log
              </div>
              <div className="text-[11px] text-white/80">
                Fill in each product check — save & print for your records.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={printSheet}
                disabled={filledRows.length === 0}
                data-testid="temp-log-print-btn"
                className="px-3 py-1.5 rounded-full bg-white text-stone-800 text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-40 hover:bg-brand-yellow"
              >
                <Printer className="w-3.5 h-3.5" />
                Print / PDF
              </button>
              <button
                onClick={() => emailLog()}
                disabled={filledRows.length === 0 || emailing}
                data-testid="temp-log-email-btn"
                className="px-3 py-1.5 rounded-full bg-brand-yellow text-stone-900 text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-40 hover:brightness-105"
                title={`Email to ${emailTo}`}
              >
                <Mail className="w-3.5 h-3.5" />
                {emailing ? "Sending..." : "Email"}
              </button>
              <button
                onClick={() => setShowEmailTo((v) => !v)}
                data-testid="temp-log-email-to-toggle"
                className="p-1.5 text-white/80 hover:text-white"
                title="Change email recipient"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showEmailTo ? "rotate-180" : ""}`} />
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="temp-log-save-btn"
                className="px-3 py-1.5 rounded-full bg-brand-red text-white text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 disabled:opacity-40 hover:bg-red-800"
              >
                {saving ? "Saving..." : selectedId ? "Update" : "Save"}
              </button>
              {selectedId && (
                <button
                  onClick={remove}
                  data-testid="temp-log-delete-btn"
                  className="p-1.5 text-white/80 hover:text-white"
                  title="Delete this log"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {showEmailTo && (
            <div className="px-4 pt-3 flex flex-wrap items-center gap-2" data-testid="temp-log-email-to-row">
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Send to</label>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => {
                  setEmailTo(e.target.value);
                  localStorage.setItem(TEMP_LOG_EMAIL_KEY, e.target.value);
                }}
                data-testid="temp-log-email-to-input"
                className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border-2 border-stone-200 text-sm outline-none focus:border-brand-red"
              />
              {emailTo !== TEMP_LOG_DEFAULT_EMAIL && (
                <button
                  onClick={() => {
                    setEmailTo(TEMP_LOG_DEFAULT_EMAIL);
                    localStorage.removeItem(TEMP_LOG_EMAIL_KEY);
                  }}
                  data-testid="temp-log-email-to-reset"
                  className="text-xs font-bold text-brand-red"
                >
                  Reset
                </button>
              )}
            </div>
          )}

          <div className="p-4 space-y-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                Date
              </span>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="temp-log-date"
                className="mt-1 w-48 px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
              />
            </label>

            <div className="overflow-x-auto">
              <table
                className="w-full text-sm border-collapse"
                data-testid="temp-log-entries-table"
              >
                <thead>
                  <tr className="bg-stone-100 text-[10px] uppercase tracking-widest text-stone-600">
                    <th className="text-left px-2 py-2 border border-stone-200">
                      Food Name
                    </th>
                    <th className="text-left px-2 py-2 border border-stone-200 w-32">
                      Time
                    </th>
                    <th className="text-left px-2 py-2 border border-stone-200 w-28">
                      Temp
                    </th>
                    <th className="text-left px-2 py-2 border border-stone-200 w-24">
                      Initials
                    </th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {form.entries.map((row, i) => (
                    <tr key={i}>
                      <td className="border border-stone-200 p-1">
                        <input
                          value={row.food_name}
                          onChange={(e) => setRow(i, { food_name: e.target.value })}
                          placeholder="e.g. Grilled chicken"
                          className="w-full px-2 py-1.5 border border-transparent focus:border-brand-red rounded outline-none text-sm"
                          data-testid={`temp-log-food-${i}`}
                        />
                      </td>
                      <td className="border border-stone-200 p-1">
                        <input
                          value={row.time}
                          onChange={(e) => setRow(i, { time: e.target.value })}
                          placeholder="11:00 AM"
                          className="w-full px-2 py-1.5 border border-transparent focus:border-brand-red rounded outline-none text-sm"
                          data-testid={`temp-log-time-${i}`}
                        />
                      </td>
                      <td className="border border-stone-200 p-1">
                        <input
                          value={row.temp}
                          onChange={(e) => setRow(i, { temp: e.target.value })}
                          placeholder="165 °F"
                          className="w-full px-2 py-1.5 border border-transparent focus:border-brand-red rounded outline-none text-sm"
                          data-testid={`temp-log-temp-${i}`}
                        />
                      </td>
                      <td className="border border-stone-200 p-1">
                        <input
                          value={row.initials}
                          onChange={(e) =>
                            setRow(i, { initials: e.target.value.toUpperCase() })
                          }
                          placeholder="BK"
                          maxLength={4}
                          className="w-full px-2 py-1.5 border border-transparent focus:border-brand-red rounded outline-none text-sm font-mono uppercase"
                          data-testid={`temp-log-initials-${i}`}
                        />
                      </td>
                      <td className="text-center border border-stone-200 p-1">
                        {form.entries.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRow(i)}
                            className="text-stone-400 hover:text-brand-red p-1"
                            aria-label="Remove row"
                            data-testid={`temp-log-remove-${i}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={addRow}
              data-testid="temp-log-add-row-btn"
              className="text-brand-red font-bold text-[11px] uppercase tracking-widest flex items-center gap-1 hover:underline"
            >
              <Plus className="w-3.5 h-3.5" />
              Add row
            </button>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                Notes (optional)
              </span>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything unusual today?"
                data-testid="temp-log-notes"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm resize-none"
              />
            </label>
          </div>

          <div className="temp-log-print">
            <div style={{ fontFamily: "sans-serif" }}>
              <h1 style={{ fontSize: 22, margin: 0 }}>
                Blossom's Kitchen — Temperature Log
              </h1>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                Date: <strong>{fmt(form.date)}</strong>
              </div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: 16,
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr>
                    {["Food Name", "Time", "Temp", "Initials"].map((h) => (
                      <th
                        key={h}
                        style={{
                          border: "1px solid #333",
                          padding: "6px 8px",
                          background: "#eee",
                          textAlign: "left",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filledRows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ border: "1px solid #333", padding: "6px 8px" }}>
                        {r.food_name}
                      </td>
                      <td style={{ border: "1px solid #333", padding: "6px 8px" }}>
                        {r.time}
                      </td>
                      <td style={{ border: "1px solid #333", padding: "6px 8px" }}>
                        {r.temp}
                      </td>
                      <td style={{ border: "1px solid #333", padding: "6px 8px" }}>
                        {r.initials}
                      </td>
                    </tr>
                  ))}
                  {Array.from({
                    length: Math.max(0, 15 - filledRows.length),
                  }).map((_, i) => (
                    <tr key={`blank-${i}`}>
                      {[0, 1, 2, 3].map((k) => (
                        <td
                          key={k}
                          style={{
                            border: "1px solid #333",
                            padding: "6px 8px",
                            height: 22,
                          }}
                        >
                          &nbsp;
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {form.notes && (
                <div style={{ marginTop: 14, fontSize: 12 }}>
                  <strong>Notes:</strong> {form.notes}
                </div>
              )}
              <div style={{ marginTop: 24, fontSize: 11, color: "#555" }}>
                Manager signature: __________________________________
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}


const THEME_OPTIONS = [
  { key: "default", label: "Classic Blossom's (no theme)", colors: ["#9b2226", "#eab308"] },
  { key: "fall", label: "🍁 Fall", colors: ["#b45309", "#d97706"] },
  { key: "halloween", label: "🎃 Halloween", colors: ["#c2410c", "#fb923c"] },
  { key: "thanksgiving", label: "🦃 Thanksgiving", colors: ["#92400e", "#f59e0b"] },
  { key: "christmas", label: "🎄 Christmas", colors: ["#166534", "#facc15"] },
  { key: "valentines", label: "💕 Valentine's Day", colors: ["#be185d", "#f9a8d4"] },
  { key: "easter", label: "🐰 Easter", colors: ["#db2777", "#c4b5fd"] },
  { key: "july4", label: "🎆 4th of July", colors: ["#1d4ed8", "#f87171"] },
];

function SeasonalThemeCard({ token }) {
  const [active, setActive] = useState("default");
  const [images, setImages] = useState({});
  const [customThemes, setCustomThemes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/site-settings")
      .then((r) => {
        setActive(r.data.active_theme || "default");
        setImages(r.data.theme_images || {});
        setCustomThemes(r.data.custom_themes || []);
      })
      .catch(() => {});
  }, []);

  const save = async (patch, msg) => {
    setBusy(true);
    try {
      await adminApi(token).put("/site-settings", patch);
      toast.success(msg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed — try again");
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const allOptions = [
    ...THEME_OPTIONS,
    ...customThemes.map((t) => ({ key: t.key, label: `✨ ${t.name}`, colors: [t.primary, t.accent], custom: true })),
  ];

  const pickTheme = (key) => {
    setActive(key);
    const label = allOptions.find((t) => t.key === key)?.label || key;
    save({ active_theme: key }, key === "default" ? "Theme turned off" : `${label} theme is live!`).catch(() => {});
  };

  const saveCustomThemes = async (next, msg, deactivated) => {
    const patch = { custom_themes: next };
    if (deactivated) patch.active_theme = "default";
    await save(patch, msg);
    setCustomThemes(next);
    if (deactivated) setActive("default");
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi(token).post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = { ...images, [active]: data.url };
      setImages(next);
      await save({ theme_images: next }, "Theme picture saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — try a smaller image");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    const next = { ...images };
    delete next[active];
    setImages(next);
    save({ theme_images: next }, "Theme picture removed");
  };

  const current = allOptions.find((t) => t.key === active) || THEME_OPTIONS[0];

  return (
    <section className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4">
      <div>
        <div className="font-display text-brand-red text-xl">Seasonal Theme</div>
        <div className="text-xs text-stone-500 mt-1 leading-snug">
          Dress the customer storefront up for the holidays — colors + cute floating
          decorations. Staff screens (Admin, POS, KDS) always stay classic.
        </div>
      </div>
      <div className="flex items-center gap-3">
        <select
          value={active}
          onChange={(e) => pickTheme(e.target.value)}
          disabled={busy}
          data-testid="seasonal-theme-select"
          className="flex-1 px-3 py-2.5 rounded-lg border-2 border-stone-300 bg-white text-sm font-bold outline-none focus:border-brand-red"
        >
          <optgroup label="Holidays">
            {THEME_OPTIONS.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </optgroup>
          {customThemes.length > 0 && (
            <optgroup label="Your custom themes">
              {customThemes.map((t) => (
                <option key={t.key} value={t.key}>✨ {t.name}</option>
              ))}
            </optgroup>
          )}
        </select>
        <div className="flex gap-1 shrink-0" title="Theme colors">
          {current.colors.map((c) => (
            <span key={c} className="w-6 h-6 rounded-full border border-stone-200" style={{ background: c }} />
          ))}
        </div>
      </div>
      {active !== "default" && (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
            Cute picture for this theme (optional — shows under the hero)
          </div>
          {images[active] ? (
            <div className="flex items-center gap-3">
              <img
                src={images[active]}
                alt="Theme decoration"
                className="h-16 rounded-lg border border-stone-200 object-cover"
                data-testid="theme-image-preview"
              />
              <button
                onClick={removeImage}
                disabled={busy}
                data-testid="theme-image-remove-btn"
                className="text-xs text-red-700 underline font-bold disabled:opacity-40"
              >
                Remove
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-stone-300 text-xs font-bold text-stone-600 cursor-pointer hover:border-brand-red">
              {uploading ? "Uploading…" : "Upload picture"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                data-testid="theme-image-upload-input"
                onChange={(e) => uploadImage(e.target.files?.[0])}
              />
            </label>
          )}
        </div>
      )}
      <CustomThemesEditor
        themes={customThemes}
        onChange={saveCustomThemes}
        busy={busy}
        activeKey={active}
      />
    </section>
  );
}

function LookAndFeelCard({ token }) {
  const [fontScale, setFontScale] = useState(100);
  const [primary, setPrimary] = useState("#9b2226");
  const [accent, setAccent] = useState("#eab308");
  const [hasCustom, setHasCustom] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    api.get("/site-settings")
      .then((r) => {
        setFontScale(r.data.font_scale ?? 100);
        if (r.data.custom_primary_color) setPrimary(r.data.custom_primary_color);
        if (r.data.custom_accent_color) setAccent(r.data.custom_accent_color);
        setHasCustom(!!(r.data.custom_primary_color || r.data.custom_accent_color));
      })
      .catch(() => {});
  }, []);

  const debouncedSave = (patch, msg) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await adminApi(token).put("/site-settings", patch);
        toast.success(msg);
      } catch {
        toast.error("Save failed — try again");
      }
    }, 600);
  };

  const changeScale = (v) => {
    setFontScale(v);
    debouncedSave({ font_scale: v }, `Text size: ${v}%`);
  };
  const changePrimary = (v) => {
    setPrimary(v);
    setHasCustom(true);
    debouncedSave({ custom_primary_color: v }, "Main color saved");
  };
  const changeAccent = (v) => {
    setAccent(v);
    setHasCustom(true);
    debouncedSave({ custom_accent_color: v }, "Accent color saved");
  };
  const resetAll = async () => {
    setFontScale(100);
    setPrimary("#9b2226");
    setAccent("#eab308");
    setHasCustom(false);
    try {
      await adminApi(token).put("/site-settings", {
        font_scale: 100,
        custom_primary_color: "",
        custom_accent_color: "",
      });
      toast.success("Back to the classic look");
    } catch {
      toast.error("Reset failed — try again");
    }
  };

  return (
    <section className="bg-white border border-stone-200 rounded-2xl p-5 space-y-5">
      <div>
        <div className="font-display text-brand-red text-xl">Text Size &amp; Colors</div>
        <div className="text-xs text-stone-500 mt-1 leading-snug">
          Scale all storefront text and pick your own brand colors. When a seasonal
          theme is on, the theme's colors take over until you switch back to Classic.
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
            Text size
          </span>
          <span className="text-xs font-black text-stone-700" data-testid="font-scale-value">
            {fontScale}%
          </span>
        </div>
        <input
          type="range"
          min={80}
          max={140}
          step={5}
          value={fontScale}
          onChange={(e) => changeScale(Number(e.target.value))}
          data-testid="font-scale-slider"
          className="w-full accent-[#9b2226]"
        />
        <div className="flex justify-between text-[9px] uppercase tracking-widest text-stone-400 mt-0.5">
          <span>Smaller</span>
          <span>Normal</span>
          <span>Bigger</span>
        </div>
        <div
          className="mt-2 text-stone-700 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2"
          style={{ fontSize: `${(fontScale / 100) * 0.875}rem` }}
          data-testid="font-scale-sample"
        >
          Sample: Blossom's Burger — patty, lettuce, tomato
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="space-y-1 block">
          <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
            Main color
          </span>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={primary}
              onChange={(e) => changePrimary(e.target.value)}
              data-testid="primary-color-input"
              className="w-10 h-10 rounded-lg border border-stone-300 cursor-pointer p-0.5 bg-white"
            />
            <span className="text-xs font-mono text-stone-600">{primary}</span>
          </div>
          <span className="text-[10px] text-stone-400 block">Buttons, prices, headings</span>
        </label>
        <label className="space-y-1 block">
          <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
            Accent color
          </span>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={accent}
              onChange={(e) => changeAccent(e.target.value)}
              data-testid="accent-color-input"
              className="w-10 h-10 rounded-lg border border-stone-300 cursor-pointer p-0.5 bg-white"
            />
            <span className="text-xs font-mono text-stone-600">{accent}</span>
          </div>
          <span className="text-[10px] text-stone-400 block">Logo, highlights, badges</span>
        </label>
      </div>
      {(hasCustom || fontScale !== 100) && (
        <button
          onClick={resetAll}
          data-testid="look-reset-btn"
          className="text-xs text-stone-600 underline font-bold hover:text-stone-900"
        >
          Reset to classic look
        </button>
      )}
    </section>
  );
}

function SiteBackgroundCard({ token }) {
  const { refresh } = useSiteSettings();
  const [bgType, setBgType] = useState("none");
  const [bgUrl, setBgUrl] = useState("");
  const [opacity, setOpacity] = useState(100);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const opacitySaveTimer = useRef(null);

  useEffect(() => {
    api.get("/site-settings")
      .then((r) => {
        setBgType(r.data.background_type || "none");
        setBgUrl(r.data.background_url || "");
        setOpacity(r.data.background_opacity ?? 100);
      })
      .catch(() => {});
  }, []);

  const save = async (patch, msg) => {
    setBusy(true);
    try {
      await adminApi(token).put("/site-settings", patch);
      refresh().catch(() => {});
      toast.success(msg);
    } catch {
      toast.error("Save failed — try again");
    } finally {
      setBusy(false);
    }
  };

  const uploadBg = async (file, kind) => {
    if (!file) return;
    const isImage = kind === "image";
    if (!file.type.startsWith(isImage ? "image/" : "video/")) {
      toast.error(isImage ? "Please pick an image file" : "Please pick a video file (mp4)");
      return;
    }
    const maxBytes = isImage ? 8 * 1024 * 1024 : 300 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`File too large — max ${maxBytes / (1024 * 1024)} MB`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      let url;
      if (isImage) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await adminApi(token).post("/admin/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        url = data.url;
      } else {
        // Chunked upload — 5MB pieces so big videos pass the proxy limit.
        const CHUNK = 5 * 1024 * 1024;
        const uploadId =
          (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
          `up_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const total = Math.max(1, Math.ceil(file.size / CHUNK));
        for (let i = 0; i < total; i++) {
          const fd = new FormData();
          fd.append("upload_id", uploadId);
          fd.append("index", String(i));
          fd.append("total", String(total));
          fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), `${uploadId}.${i}`);
          await adminApi(token).post("/admin/upload-video/chunk", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setProgress(Math.round(((i + 1) / total) * 95));
        }
        const { data } = await adminApi(token).post("/admin/upload-video/finalize", {
          upload_id: uploadId,
          total,
          filename: file.name,
          content_type: file.type || "video/mp4",
        });
        url = data.url;
      }
      setProgress(100);
      setBgType(kind);
      setBgUrl(url);
      await save({ background_type: kind, background_url: url }, "Background is live!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — check your connection and try again");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const changeOpacity = (value) => {
    setOpacity(value);
    if (opacitySaveTimer.current) clearTimeout(opacitySaveTimer.current);
    opacitySaveTimer.current = setTimeout(async () => {
      try {
        await adminApi(token).put("/site-settings", { background_opacity: value });
        toast.success(`Background visibility: ${value}%`);
      } catch {
        toast.error("Save failed — try again");
      }
    }, 600);
  };

  const removeBg = () => {
    setBgType("none");
    setBgUrl("");
    save({ background_type: "none", background_url: "" }, "Background removed");
  };

  return (
    <section className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4">
      <div>
        <div className="font-display text-brand-red text-xl">Site Background</div>
        <div className="text-xs text-stone-500 mt-1 leading-snug">
          Upload a full-page picture or video that shows behind the customer
          storefront (menu cards stay solid so everything's readable). Staff
          screens are never affected.
        </div>
      </div>
      {bgType !== "none" && bgUrl ? (
        <div className="space-y-4">
          {/* Live storefront preview — same cream wash the real site applies,
              so what you see here is what customers see. */}
          <div
            className="relative w-full h-44 rounded-xl overflow-hidden border border-stone-200"
            data-testid="site-bg-live-preview"
          >
            {bgType === "video" ? (
              <video
                src={bgUrl}
                muted
                loop
                autoPlay
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                style={{ opacity: opacity / 100 }}
                data-testid="site-bg-preview"
              />
            ) : (
              <img
                src={bgUrl}
                alt="Site background"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ opacity: opacity / 100 }}
                data-testid="site-bg-preview"
              />
            )}
            {/* the storefront's light cream wash (matches the real site) */}
            <div
              className="absolute inset-0"
              style={{ backgroundColor: "rgba(253, 251, 247, 0.42)" }}
            />
            {/* sample menu card so readability is easy to judge */}
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="bg-white border border-stone-200 rounded-xl shadow-sm px-4 py-3 w-full max-w-[240px]">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-black text-stone-800">Blossom's Burger</span>
                  <span className="font-display text-brand-red">$13.00</span>
                </div>
                <div className="text-[11px] text-stone-500 mt-0.5">Patty, lettuce, tomato</div>
              </div>
            </div>
            <div className="absolute top-2 left-2 bg-stone-900/80 text-white text-[9px] uppercase tracking-widest font-black px-2 py-1 rounded-full">
              Storefront preview
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
              {bgType === "video" ? "Video background" : "Picture background"} active
            </div>
            <button
              onClick={removeBg}
              disabled={busy}
              data-testid="site-bg-remove-btn"
              className="text-xs text-red-700 underline font-bold disabled:opacity-40"
            >
              Remove background
            </button>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
                Visibility
              </span>
              <span className="text-xs font-black text-stone-700" data-testid="site-bg-opacity-value">
                {opacity}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={opacity}
              onChange={(e) => changeOpacity(Number(e.target.value))}
              data-testid="site-bg-opacity-slider"
              className="w-full accent-[#9b2226]"
            />
            <div className="flex justify-between text-[9px] uppercase tracking-widest text-stone-400 mt-0.5">
              <span>Subtle</span>
              <span>Full strength</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-stone-300 text-xs font-bold text-stone-600 cursor-pointer hover:border-brand-red">
            {uploading ? "Uploading…" : "Upload picture"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              data-testid="site-bg-image-input"
              onChange={(e) => uploadBg(e.target.files?.[0], "image")}
            />
          </label>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-stone-300 text-xs font-bold text-stone-600 cursor-pointer hover:border-brand-red">
            {uploading ? `Uploading… ${progress}%` : "Upload video (mp4, max 300MB)"}
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              className="hidden"
              data-testid="site-bg-video-input"
              onChange={(e) => uploadBg(e.target.files?.[0], "video")}
            />
          </label>
        </div>
      )}
    </section>
  );
}

function SiteTab({ token, hasCustomPassword, onPasswordChanged, jumpTo }) {
  const { settings, refresh } = useSiteSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  // Track whether the local form has diverged from context settings so
  // background polls (every 30 s via SiteSettingsContext) don't clobber
  // in-progress edits. `dirtyRef` mirrors `dirty` synchronously so the
  // useEffect below sees the latest value without needing it as a dep.
  const dirtyRef = useRef(false);

  // Only the keys this form actually edits. Cards that save on their own
  // (background, themes, colors, font size…) must never be re-sent from here,
  // otherwise a stale copy overwrites what those cards just saved.
  const changedKeys = useMemo(
    () => FORM_KEYS.filter((k) => JSON.stringify(form[k]) !== JSON.stringify(settings[k])),
    [form, settings]
  );
  const dirty = changedKeys.length > 0;
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  // Sync local form from context settings ONLY when the user isn't in the
  // middle of editing. Prior version overwrote every poll -> everything
  // typed in the last 30s vanished before Save landed.
  useEffect(() => {
    if (!dirtyRef.current) {
      setForm(settings);
    }
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = Object.fromEntries(changedKeys.map((k) => [k, form[k]]));
      await adminApi(token).put("/site-settings", patch);
      await refresh();
      toast.success("Site content saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setForm(settings);

  const fg = (name, tab, keywords) => {
    const grp = SITE_FIELDS.find((g) => g.group === name);
    const labels = grp.fields.map((f) => f.label).join(" ");
    return {
      id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      tab,
      title: name,
      keywords: `${keywords} ${labels}`,
      node: <SiteFieldGroup group={grp.group} fields={grp.fields} form={form} setForm={setForm} token={token} />,
    };
  };

  const sections = [
    { id: "tap-edit", tab: "look", title: "Tap-to-Edit Text", keywords: "edit text copy storefront in place wording", node: <TapToEditCard /> },
    { id: "theme", tab: "look", title: "Seasonal & Custom Themes", keywords: "holiday christmas halloween fall valentines easter theme colors emoji custom decorations", node: <SeasonalThemeCard token={token} /> },
    { id: "look-feel", tab: "look", title: "Text Size & Brand Colors", keywords: "font scale size zoom brand color primary accent", node: <LookAndFeelCard token={token} /> },
    { id: "background", tab: "look", title: "Page Background", keywords: "background image video opacity wallpaper", node: <SiteBackgroundCard token={token} /> },
    fg("Homepage Banner Videos", "look", "video banner clip open closed hero"),
    { id: "video-library", tab: "look", title: "Video Library", keywords: "video library re-optimize 60fps encode smooth compress", node: <VideoLibraryCard token={token} /> },
    { id: "featured", tab: "look", title: "Featured Items Carousel", keywords: "photos slideshow carousel pictures featured homepage", node: <FeaturedItemsCard token={token} form={form} setForm={setForm} /> },
    { id: "soundtrack", tab: "look", title: "Soundtrack (Music Player)", keywords: "music songs audio soundtrack player playlist mp3 upload listen", node: <SoundtrackCard token={token} /> },
    fg("Brand & Hero", "content", "brand name title welcome tagline button cta"),
    fg("Owner Attribution", "content", "owner names owned operated"),
    fg("About Us", "content", "about story mission founded year"),
    fg("Rewards Copy", "content", "rewards loyalty signup headline points"),
    fg("Checkout & Legal", "content", "checkout footer advisory food safety legal disclaimer"),
    fg("Truck Info", "ordering", "phone number hours open"),
    fg("Today's Truck Location", "ordering", "location address map spot where truck today"),
    fg("Ordering On/Off", "ordering", "paused closed ordering message off"),
    fg("Tax Rates", "ordering", "sales tax percent rate"),
    fg("Free Drinks", "ordering", "free drink promo window banner"),
    fg("Menu Categories", "ordering", "categories menu sections drinks add-ons"),
    { id: "pos-layout", tab: "register", title: "POS Layout", keywords: "pos register grid columns tiles layout", node: <PosLayoutCard token={token} form={form} setForm={setForm} /> },
    { id: "kds-colors", tab: "register", title: "KDS Ticket Colors", keywords: "kitchen display kds ticket color online pos", node: <KDSTicketColorCard form={form} setForm={setForm} /> },
    { id: "tip-reader", tab: "register", title: "Tip on Reader", keywords: "tip tipping square reader bluetooth setup", node: <TipOnReaderSetupCard /> },
    { id: "square-diag", tab: "square", title: "Square Diagnostics", keywords: "square connection check health payments api token", node: <SquareDiagnosticsCard token={token} /> },
    { id: "square-fail", tab: "square", title: "Square Failures", keywords: "failed payments sync errors retry", node: <SquareFailuresCard token={token} /> },
    { id: "password", tab: "security", title: "Admin Password", keywords: "password login passcode admin security change", node: <ChangePasswordCard token={token} hasCustomPassword={hasCustomPassword} onChanged={onPasswordChanged} /> },
    { id: "cashier", tab: "security", title: "Cashier Passcode", keywords: "cashier passcode pos staff login pin", node: <ChangeCashierPasscodeCard token={token} /> },
    { id: "clock-app", tab: "security", title: "Staff Clock-In App", keywords: "clock in time clock punch pin device install app staff hours", node: <ClockAppCard token={token} /> },
    fg("Clock-Out Reminder", "security", "forgot clock out overdue shift reminder email cutoff labor"),
  ];

  return (
    <SettingsShell
      sections={sections}
      jumpTo={jumpTo}
      dirty={dirty}
      saving={saving}
      onSave={save}
      onDiscard={reset}
    />
  );
}

function TapToEditCard() {
  return (
    <section className="bg-stone-900 text-white rounded-2xl p-5 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="font-display text-brand-yellow text-xl">Tap-to-Edit Text</div>
        <div className="text-xs text-stone-300 mt-1 leading-snug">
          Open the storefront in edit mode — tap any text on any customer page
          (menu, checkout, rewards, reviews…) to change it in place.
        </div>
      </div>
      <a
        href="/?edit-text=1"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="open-text-edit-mode-btn"
        className="bg-brand-yellow text-stone-900 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-black hover:brightness-105 whitespace-nowrap shrink-0"
      >
        Edit Text ↗
      </a>
    </section>
  );
}

function SiteFieldGroup({ group, fields, form, setForm, token }) {
  return (
        <section
          data-testid={`site-section-${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="bg-white rounded-xl border border-stone-200 overflow-hidden"
        >
          <div className="bg-stone-50 px-4 py-2.5 text-[11px] uppercase tracking-widest font-black text-stone-700 border-b border-stone-200">
            {group}
          </div>
          <div className="p-4 space-y-3">
            {fields.map(({ key, label, type }) => (
              <label key={key} className="block">
                <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                  {label}
                </span>
                {type === "categories" ? (
                  <CategoriesEditor
                    value={form[key] || []}
                    onChange={(next) => setForm({ ...form, [key]: next })}
                    form={form}
                    setForm={setForm}
                    testIdPrefix={`site-field-${key}`}
                  />
                ) : type === "toggle" ? (
                  <div className="mt-1 flex items-center gap-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!form[key]}
                      onClick={() => setForm({ ...form, [key]: !form[key] })}
                      data-testid={`site-field-${key}`}
                      className={`relative w-14 h-8 rounded-full transition-colors ${
                        form[key] ? "bg-emerald-600" : "bg-stone-300"
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                          form[key] ? "translate-x-6" : ""
                        }`}
                      />
                    </button>
                    <span className="text-xs font-bold text-stone-600">
                      {form[key] ? "ON" : "OFF"}
                    </span>
                  </div>
                ) : type === "datetime" ? (
                  <input
                    type="datetime-local"
                    value={isoToLocalInput(form[key])}
                    onChange={(e) =>
                      setForm({ ...form, [key]: localInputToIso(e.target.value) })
                    }
                    data-testid={`site-field-${key}`}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
                  />
                ) : type === "textarea" ? (
                  <textarea
                    rows={3}
                    value={form[key] ?? ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    data-testid={`site-field-${key}`}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm resize-none"
                  />
                ) : type === "time" ? (
                  <input
                    type="time"
                    value={form[key] || ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    data-testid={`site-field-${key}`}
                    className="mt-1 w-40 px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm font-mono"
                  />
                ) : type === "percent" ? (
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={
                        form[key] === "" || form[key] === undefined || form[key] === null
                          ? ""
                          : String(Math.round(Number(form[key]) * 100000) / 1000)
                      }
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d.]/g, "");
                        const parts = raw.split(".");
                        const clean = parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 3)}` : raw;
                        if (clean === "" || clean === ".") {
                          setForm({ ...form, [key]: 0 });
                        } else {
                          const pct = parseFloat(clean);
                          if (!isNaN(pct)) {
                            // Clamp 0-50% to match backend sanity guard.
                            const clamped = Math.max(0, Math.min(50, pct));
                            setForm({ ...form, [key]: clamped / 100 });
                          }
                        }
                      }}
                      data-testid={`site-field-${key}`}
                      className="w-24 px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm font-mono text-right"
                    />
                    <span className="text-sm text-stone-500">%</span>
                    <span className="text-[11px] text-stone-400 ml-2">
                      (stored as {Number(form[key] || 0).toFixed(4)})
                    </span>
                  </div>
                ) : type === "toggle" ? (
                  <div className="mt-1 flex items-center gap-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!form[key]}
                      onClick={() =>
                        setForm({ ...form, [key]: !form[key] })
                      }
                      data-testid={`site-field-${key}`}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        form[key] ? "bg-brand-red" : "bg-stone-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          form[key] ? "translate-x-6" : ""
                        }`}
                      />
                    </button>
                    <span className="text-xs text-stone-600">
                      {form[key] ? "Showing on homepage" : "Hidden"}
                    </span>
                  </div>
                ) : type === "video" ? (
                  <div className="mt-1 space-y-2">
                    <div className="flex gap-2 items-center flex-wrap">
                      <input
                        type="text"
                        value={form[key] ?? ""}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                        data-testid={`site-field-${key}`}
                        placeholder="https://.../your-clip.mp4  — or use Upload"
                        className="flex-1 min-w-[220px] px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm font-mono"
                      />
                      <VideoUploadButton
                        token={token}
                        testId={`site-field-${key}-upload`}
                        onUploaded={(url) => setForm({ ...form, [key]: url })}
                      />
                    </div>
                    {(form[key] || "").trim() ? (
                      <div
                        className="relative rounded-lg overflow-hidden border-2 border-stone-200 bg-stone-900"
                        data-testid={`site-field-${key}-preview`}
                      >
                        <video
                          key={form[key]}
                          src={form[key]}
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-40 object-cover block"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                        <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest font-black bg-white/90 text-stone-700 px-2 py-0.5 rounded-full">
                          Preview
                        </div>
                      </div>
                    ) : (
                      <div className="text-[11px] text-stone-400 italic px-1">
                        Empty — homepage will use the built-in default clip.
                      </div>
                    )}
                    {(form[key] || "").trim() && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, [key]: "" })}
                        data-testid={`site-field-${key}-clear`}
                        className="text-[11px] uppercase tracking-widest font-bold text-brand-red hover:underline"
                      >
                        Clear · use default
                      </button>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={form[key] ?? ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    data-testid={`site-field-${key}`}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
                  />
                )}
              </label>
            ))}
          </div>
        </section>
  );
}


// ---------- Square Diagnostics Card ----------
function SquareDiagnosticsCard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await adminApi(token).get("/admin/square-check");
      setData(res.data);
    } catch (err) {
      setData({ error: err?.response?.data?.detail || "Failed to check" });
    } finally {
      setLoading(false);
    }
  };

  const bad =
    data &&
    (!data.token_present ||
      (!data.location_found && !data.auto_routed_to) ||
      (data.location_status !== "ACTIVE" && !data.auto_routed_to));

  const autoRouted = data?.auto_routed_to;

  return (
    <section
      data-testid="admin-square-diag-card"
      className="rounded-xl border-2 bg-white border-stone-200 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => {
          const nextOpen = !open;
          setOpen(nextOpen);
          if (nextOpen && !data) run();
        }}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-stone-50 transition-colors"
        data-testid="admin-square-diag-toggle"
      >
        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
          <span className="text-emerald-700 text-sm font-bold">$</span>
        </div>
        <div className="flex-1">
          <div className="font-display text-base text-brand-red">
            Payment Diagnostics
          </div>
          <div className="text-[11px] text-stone-500">
            Verify the Square token &amp; location this server is using.
          </div>
        </div>
        <div className="text-stone-400 text-lg">{open ? "−" : "+"}</div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-2 text-xs">
          <button
            type="button"
            onClick={run}
            disabled={loading}
            data-testid="admin-square-diag-run"
            className="bg-brand-red text-white px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold hover:bg-red-800 disabled:opacity-50"
          >
            {loading ? "Checking..." : "Re-check now"}
          </button>

          {data && (
            <>
              {bad && (
                <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-3 text-red-800">
                  <div className="font-bold mb-1">Problem detected</div>
                  <div className="text-[11px]">
                    {!data.token_present
                      ? "No SQUARE_ACCESS_TOKEN is set on this server."
                      : !data.location_found
                      ? `The configured location id "${data.configured_location_id}" is not in this token's authorized locations. Fix SQUARE_LOCATION_ID in the deployment settings.`
                      : data.location_status !== "ACTIVE"
                      ? `Your Square location "${data.location_name}" is ${data.location_status}. Reactivate it in Square Dashboard → Locations, or switch SQUARE_LOCATION_ID to an ACTIVE location.`
                      : "Something is off — see raw output below."}
                  </div>
                </div>
              )}
              {!bad && (
                <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-emerald-800 text-[11px]">
                  {autoRouted ? (
                    <>
                      Auto-healing active. The configured location{" "}
                      <span className="font-bold">
                        {data.location_name || data.configured_location_id}
                      </span>{" "}
                      is {data.location_status || "not found"} — orders are being
                      routed to <span className="font-bold">{autoRouted.name}</span>{" "}
                      ({autoRouted.status}) instead.
                    </>
                  ) : (
                    <>
                      Everything looks good. Token present ({data.token_prefix}…),
                      location <span className="font-bold">{data.location_name}</span> is ACTIVE.
                    </>
                  )}
                </div>
              )}

              <div className="mt-2 rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-1 text-[11px] text-stone-700">
                <div>
                  Environment: <span className="font-mono">{data.environment}</span>
                </div>
                <div>
                  Token: {data.token_present
                    ? <span className="font-mono">{data.token_prefix}…</span>
                    : <span className="text-red-700 font-bold">missing</span>}
                </div>
                <div>
                  Configured location id:{" "}
                  <span className="font-mono">{data.configured_location_id || "—"}</span>
                </div>
                {data.location_name && (
                  <div>
                    Matches: <span className="font-bold">{data.location_name}</span>{" "}
                    <span
                      className={
                        data.location_status === "ACTIVE"
                          ? "text-emerald-700"
                          : "text-red-700"
                      }
                    >
                      ({data.location_status})
                    </span>
                  </div>
                )}
                {data.error && (
                  <div className="text-red-700">Error: {data.error}</div>
                )}
              </div>

              {Array.isArray(data.all_locations) && data.all_locations.length > 0 && (
                <div className="mt-2 rounded-lg border border-stone-200 bg-white p-3 text-[11px]">
                  <div className="font-bold text-stone-600 mb-1 uppercase tracking-widest text-[10px]">
                    All locations on this Square account
                  </div>
                  <ul className="space-y-0.5">
                    {data.all_locations.map((loc) => (
                      <li key={loc.id} className="flex items-center justify-between gap-2">
                        <span className="font-mono truncate">{loc.id}</span>
                        <span className="flex-1 truncate">{loc.name}</span>
                        <span
                          className={
                            loc.status === "ACTIVE"
                              ? "text-emerald-700 font-bold"
                              : "text-red-700 font-bold"
                          }
                        >
                          {loc.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}


// ---------- Recent Square Payment Failures (raw dump) ----------
function SquareFailuresCard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await adminApi(token).get("/admin/square-failures");
      setData(res.data);
    } catch (err) {
      setData({ error: err?.response?.data?.detail || "Failed to fetch" });
    } finally {
      setLoading(false);
    }
  };

  const failures = data?.failures || [];

  return (
    <section
      data-testid="admin-square-failures-card"
      className="rounded-xl border-2 bg-white border-stone-200 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => {
          const nextOpen = !open;
          setOpen(nextOpen);
          if (nextOpen) run();
        }}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-stone-50 transition-colors"
        data-testid="admin-square-failures-toggle"
      >
        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
          <span className="text-red-700 text-sm font-bold">!</span>
        </div>
        <div className="flex-1">
          <div className="font-display text-base text-brand-red">
            Recent Payment Failures
          </div>
          <div className="text-[11px] text-stone-500">
            Full raw Square error for the last 20 declined payments. Copy this
            when calling Square Support.
          </div>
        </div>
        <div className="text-stone-400 text-lg">{open ? "−" : "+"}</div>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-stone-200 pt-3 space-y-3">
          <button
            onClick={run}
            disabled={loading}
            data-testid="admin-square-failures-refresh"
            className="text-[11px] uppercase tracking-widest font-bold text-stone-700 hover:text-brand-red disabled:opacity-40"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
          {data?.error && (
            <div className="text-red-700 text-xs">Error: {data.error}</div>
          )}
          {!loading && failures.length === 0 && !data?.error && (
            <div className="text-xs text-stone-500 italic">
              No failures on record. This is the good outcome!
            </div>
          )}
          {failures.map((f, i) => {
            const isPriceTamper = f.kind === "price_tampering";
            return (
            <div
              key={`${f.created_at || ""}-${f.code || f.kind || "unknown"}-${i}`}
              className={`rounded-lg border p-3 text-[11px] font-mono text-stone-800 space-y-1 ${
                isPriceTamper
                  ? "border-amber-400 bg-amber-50"
                  : "border-stone-200 bg-stone-50"
              }`}
              data-testid={`admin-square-failure-${i}`}
            >
              <div className="flex items-center justify-between gap-2">
                {isPriceTamper ? (
                  <span
                    className="font-bold text-amber-800 bg-amber-200 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest"
                    data-testid="admin-price-tamper-badge"
                  >
                    ⚠ Suspicious Price
                  </span>
                ) : (
                  <span className="font-bold text-red-700">
                    {f.code || "UNKNOWN"}
                  </span>
                )}
                <span className="text-stone-500 text-[10px]">
                  {f.created_at}
                </span>
              </div>
              {isPriceTamper ? (
                <>
                  <div>
                    <span className="text-stone-500">Item:</span> {f.item_name}
                  </div>
                  <div>
                    <span className="text-stone-500">Attempted:</span>{" "}
                    <span className="text-red-700 font-bold">
                      ${((f.amount_money_cents || 0) / 100).toFixed(2)}
                    </span>{" "}
                    · <span className="text-stone-500">Menu price:</span>{" "}
                    <span className="text-emerald-700 font-bold">
                      ${((f.expected_cents || 0) / 100).toFixed(2)}
                    </span>
                  </div>
                  {f.customer_phone && (
                    <div>
                      <span className="text-stone-500">Phone:</span>{" "}
                      {f.customer_phone}
                    </div>
                  )}
                  {f.customer_name && (
                    <div>
                      <span className="text-stone-500">Name:</span>{" "}
                      {f.customer_name}
                    </div>
                  )}
                  {f.detail && (
                    <div>
                      <span className="text-stone-500">detail:</span> {f.detail}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div><span className="text-stone-500">Order:</span> {f.order_number}</div>
                  <div><span className="text-stone-500">amount_money:</span> {f.amount_money_cents} cents · tip_money: {f.tip_money_cents} cents</div>
                  {f.category && <div><span className="text-stone-500">category:</span> {f.category}</div>}
                  {f.field && <div><span className="text-stone-500">field:</span> {f.field}</div>}
                  {f.detail && <div><span className="text-stone-500">detail:</span> {f.detail}</div>}
                  {f.square_order_id && <div><span className="text-stone-500">square_order_id:</span> {f.square_order_id}</div>}
                  {f.raw && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-stone-500 hover:text-brand-red">Full raw response</summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all text-[10px] text-stone-700">
                        {JSON.stringify(f.raw, null, 2)}
                      </pre>
                    </details>
                  )}
                </>
              )}
            </div>
            );
          })}
        </div>
      )}
    </section>
  );
}



// ---------- Change Password Card (Site tab, top) ----------
function ChangePasswordCard({ token, hasCustomPassword, onChanged }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(!hasCustomPassword);

  // Auto-expand for first-time users, collapse for returning ones
  useEffect(() => {
    setOpen(!hasCustomPassword);
  }, [hasCustomPassword]);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (next === current) {
      toast.error("New password must be different from the current one");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi(token).post("/admin/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed — you're locked down 🔒");
      setCurrent("");
      setNext("");
      setConfirm("");
      onChanged && onChanged(data.token);
      setOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401 && detail === "Unauthorized") {
        // Header token is stale (password was rotated elsewhere). Boot the
        // user back to login so they can re-authenticate with the current
        // passcode.
        toast.error("Your session is stale — please log out and log back in with the current passcode");
        localStorage.removeItem("bk_admin_token");
        window.location.reload();
        return;
      }
      toast.error(detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="admin-security-card"
      className={`rounded-xl border-2 overflow-hidden ${
        hasCustomPassword ? "bg-white border-stone-200" : "bg-amber-50 border-amber-400"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-stone-50 transition-colors"
        data-testid="admin-security-toggle"
      >
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-black text-white ${
            hasCustomPassword ? "bg-emerald-600" : "bg-amber-500"
          }`}
        >
          {hasCustomPassword ? "✓" : "!"}
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">
            Admin Security
          </div>
          <div className="text-sm font-bold text-stone-800">
            {hasCustomPassword
              ? "Custom password set — tap to change"
              : "Set a secure password to lock down admin"}
          </div>
        </div>
        <span className="text-xs text-stone-400">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <form
          onSubmit={submit}
          className="border-t border-stone-200 p-4 space-y-3"
          data-testid="admin-change-password-form"
        >
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
              Current password
            </span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="admin-current-password"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
              New password (12+ chars, letters + numbers)
            </span>
            <input
              type="password"
              required
              minLength={12}
              pattern="^(?=.*[A-Za-z])(?=.*\d).{12,}$"
              title="At least 12 characters and must include both a letter and a number"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="admin-new-password"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
              Confirm new password
            </span>
            <input
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="admin-confirm-password"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-change-password-btn"
            className="w-full bg-brand-red text-white py-2.5 rounded-full text-xs uppercase tracking-widest font-black hover:bg-red-800 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Change Password"}
          </button>
          {!hasCustomPassword && (
            <div className="text-[11px] text-amber-800 leading-relaxed">
              Once you set a custom password, the default passcode
              (<span className="font-mono">blossom2026</span>) will stop working — only your new
              password will unlock admin. Write it down somewhere safe.
            </div>
          )}
        </form>
      )}
    </section>
  );
}

// ---------- PWA Install Button (top bar) ----------
function PwaInstallButton() {
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const alreadyInstalled =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (alreadyInstalled) setInstalled(true);

    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const install = async () => {
    if (installEvent) {
      installEvent.prompt();
      const { outcome } = await installEvent.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setInstallEvent(null);
      return;
    }
    // iOS Safari has no install event — show a howto toast
    toast.info(
      "On iPhone: tap the Share button → Add to Home Screen"
    );
  };

  return (
    <button
      onClick={install}
      data-testid="admin-pwa-install-btn"
      className="text-white/80 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-3 py-2"
    >
      <Download className="w-4 h-4" /> Install
    </button>
  );
}

// ---------- Rewards Tab (Loyalty CRUD) ----------
function RewardsTab({ token }) {
  const [rewards, setRewards] = useState([]);
  const [menu, setMenu] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, ms] = await Promise.all([
        adminApi(token).get("/admin/rewards"),
        api.get("/menu"),
      ]);
      setRewards(rs.data);
      setMenu(ms.data);
    } catch {
      toast.error("Failed to load rewards");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const del = async (r) => {
    if (!window.confirm(`Delete reward "${r.label}"?`)) return;
    try {
      await adminApi(token).delete(`/admin/rewards/${r.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleActive = async (r) => {
    try {
      await adminApi(token).put(`/admin/rewards/${r.id}`, { active: !r.active });
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const categories = useMemo(
    () => Array.from(new Set(menu.map((m) => m.category))).sort(),
    [menu]
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <PointsCreditCard token={token} />
      <PromotionsCard token={token} />

      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-display text-brand-red">Loyalty Rewards</div>
          <div className="text-xs text-stone-500">
            Customers earn 1 point per $1. Add/edit tiers here — changes appear on the customer app instantly.
          </div>
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          data-testid="rewards-add-btn"
          className="bg-brand-red text-white px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-red-800 flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> {showAdd ? "Close" : "Add Reward"}
        </button>
      </div>

      {showAdd && (
        <RewardForm
          token={token}
          categories={categories}
          menu={menu}
          onSaved={() => {
            setShowAdd(false);
            load();
          }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {loading ? (
        <div className="text-center text-stone-400 py-8">Loading…</div>
      ) : rewards.length === 0 ? (
        <div className="text-center text-stone-400 py-8">No rewards yet — tap Add Reward.</div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <ul className="divide-y divide-stone-100">
            {rewards.map((r) => (
              <li key={r.id} data-testid={`reward-row-${r.id}`}>
                {editingId === r.id ? (
                  <RewardForm
                    token={token}
                    categories={categories}
                    menu={menu}
                    initial={r}
                    onSaved={() => {
                      setEditingId(null);
                      load();
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <div className="px-4 py-3 flex items-center gap-3">
                    <Gift
                      className={`w-5 h-5 shrink-0 ${
                        r.active ? "text-brand-red" : "text-stone-300"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-stone-800 truncate">{r.label}</div>
                      <div className="text-xs text-stone-500 truncate">
                        {r.points} pts ·{" "}
                        {r.fixed_item
                          ? `Free ${r.fixed_item}`
                          : `Any ${r.menu_category || "?"}`}{" "}
                        · sort {r.sort_order}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleActive(r)}
                      data-testid={`reward-toggle-${r.id}`}
                      className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full w-20 text-center ${
                        r.active
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : "bg-stone-200 text-stone-600 hover:bg-stone-300"
                      }`}
                    >
                      {r.active ? "Active" : "Off"}
                    </button>
                    <button
                      onClick={() => setEditingId(r.id)}
                      data-testid={`reward-edit-${r.id}`}
                      className="text-stone-400 hover:text-brand-red p-1"
                      aria-label={`Edit ${r.label}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => del(r)}
                      data-testid={`reward-delete-${r.id}`}
                      className="text-stone-400 hover:text-brand-red p-1"
                      aria-label={`Delete ${r.label}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RewardForm({ token, categories, menu, initial, onSaved, onCancel }) {
  const [form, setForm] = useState({
    label:         initial?.label         || "",
    points:        String(initial?.points ?? 100),
    kind:          initial?.fixed_item ? "fixed" : "category",
    menu_category: initial?.menu_category || (categories[0] || ""),
    fixed_item:    initial?.fixed_item    || (menu[0]?.name || ""),
    sort_order:    String(initial?.sort_order ?? 100),
    active:        initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      label:        form.label.trim(),
      points:       parseInt(form.points, 10) || 0,
      menu_category: form.kind === "category" ? form.menu_category : null,
      fixed_item:   form.kind === "fixed" ? form.fixed_item : null,
      sort_order:   parseInt(form.sort_order, 10) || 100,
      active:       form.active,
    };
    try {
      if (isEdit) {
        await adminApi(token).put(`/admin/rewards/${initial.id}`, payload);
      } else {
        await adminApi(token).post("/admin/rewards", payload);
      }
      toast.success(isEdit ? "Reward updated" : "Reward added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={isEdit ? `reward-edit-form-${initial.id}` : "reward-add-form"}
      className="bg-brand-yellow/10 border-l-4 border-brand-red px-4 py-3 grid grid-cols-1 md:grid-cols-6 gap-3"
    >
      <input
        required
        placeholder="Label (e.g. Free Milkshake)"
        value={form.label}
        onChange={(e) => setForm({ ...form, label: e.target.value })}
        data-testid="reward-form-label"
        className="md:col-span-3 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
      />
      <input
        required
        type="number"
        min="1"
        placeholder="Points"
        value={form.points}
        onChange={(e) => setForm({ ...form, points: e.target.value })}
        data-testid="reward-form-points"
        className="md:col-span-1 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
      />
      <input
        type="number"
        placeholder="Sort"
        value={form.sort_order}
        onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
        data-testid="reward-form-sort"
        className="md:col-span-1 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
      />
      <label className="md:col-span-1 flex items-center gap-2 text-xs text-stone-700">
        <input
          type="checkbox"
          checked={form.active}
          onChange={(e) => setForm({ ...form, active: e.target.checked })}
          data-testid="reward-form-active"
          className="w-4 h-4"
        />
        Active
      </label>

      <div className="md:col-span-6 flex gap-4 text-xs">
        <label className="flex items-center gap-1">
          <input
            type="radio"
            name="reward-kind"
            checked={form.kind === "category"}
            onChange={() => setForm({ ...form, kind: "category" })}
            data-testid="reward-form-kind-category"
          />
          Any item in a category
        </label>
        <label className="flex items-center gap-1">
          <input
            type="radio"
            name="reward-kind"
            checked={form.kind === "fixed"}
            onChange={() => setForm({ ...form, kind: "fixed" })}
            data-testid="reward-form-kind-fixed"
          />
          A specific menu item
        </label>
      </div>

      {form.kind === "category" ? (
        <select
          value={form.menu_category}
          onChange={(e) => setForm({ ...form, menu_category: e.target.value })}
          data-testid="reward-form-category"
          className="md:col-span-6 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
        >
          {categories.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      ) : (
        <select
          value={form.fixed_item}
          onChange={(e) => setForm({ ...form, fixed_item: e.target.value })}
          data-testid="reward-form-fixed-item"
          className="md:col-span-6 px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
        >
          {menu.map((m) => (
            <option key={m.id} value={m.name}>
              {m.name} ({m.category})
            </option>
          ))}
        </select>
      )}

      <div className="md:col-span-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold text-stone-600 hover:bg-stone-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          data-testid="reward-form-save"
          className="bg-brand-red text-white px-5 py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-red-800 disabled:opacity-50"
        >
          {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Reward"}
        </button>
      </div>
    </form>
  );
}


/**
 * StaffTab — manage employees (name, 4-digit PIN, hourly pay rate) and see
 * who is currently on the clock. All data is behind admin auth.
 */
function StaffTab({ token }) {
  const [employees, setEmployees] = useState([]);
  const [onClock, setOnClock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", pin: "", pay_rate: "", phone: "", is_manager: false });
  const [fixEntry, setFixEntry] = useState(null);
  const clockOutNow = async (e) => {
    if (!window.confirm(`Clock ${e.employee_name} out right now?`)) return;
    try {
      await adminApi(token).put(`/admin/time-entries/${e.id}`, { clock_out: new Date().toISOString() });
      toast.success(`${e.employee_name} clocked out`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't clock out");
    }
  };
  const [punchRefresh, setPunchRefresh] = useState(0);

  const load = useCallback(async () => {
    try {
      const [emps, live] = await Promise.all([
        adminApi(token).get("/admin/employees"),
        adminApi(token).get("/admin/employees/on-clock"),
      ]);
      setEmployees(emps.data);
      setOnClock(live.data);
    } catch {
      toast.error("Couldn't load staff");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // live on-clock refresh
    return () => clearInterval(t);
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const body = {
      name: form.name.trim(),
      pin: form.pin.trim(),
      pay_rate: parseFloat(form.pay_rate) || 0,
      phone: form.phone,
      is_manager: !!form.is_manager,
    };
    if (!body.name || !/^\d{4}$/.test(body.pin)) {
      toast.error("Name required and PIN must be 4 digits");
      return;
    }
    try {
      if (editingId) {
        await adminApi(token).put(`/admin/employees/${editingId}`, body);
        toast.success("Employee updated");
      } else {
        await adminApi(token).post("/admin/employees", body);
        toast.success("Employee added");
      }
      setForm({ name: "", pin: "", pay_rate: "", phone: "", is_manager: false });
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const edit = (emp) => {
    setEditingId(emp.id);
    setForm({ name: emp.name, pin: emp.pin, pay_rate: String(emp.pay_rate), phone: emp.phone || "", is_manager: !!emp.is_manager });
  };

  const del = async (emp) => {
    if (!window.confirm(`Delete ${emp.name}?`)) return;
    const purge = window.confirm(
      `Also erase ALL of ${emp.name}'s time punches?\n\nOK = yes, wipe their hours too (use for test or placeholder staff).\nCancel = keep their past hours for payroll; just close any open shift.`
    );
    try {
      await adminApi(token).delete(`/admin/employees/${emp.id}`, { params: purge ? { purge: 1 } : {} });
      toast.success(purge ? "Employee and their punches deleted" : "Employee deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const clockedInIds = new Set(onClock.map((e) => e.employee_id));

  const fmtStarted = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };
  const shiftHours = (e) => {
    try {
      const now = Date.now();
      let ms = now - new Date(e.clock_in).getTime();
      for (const b of e.breaks || []) {
        ms -= (b.end ? new Date(b.end).getTime() : now) - new Date(b.start).getTime();
      }
      return (Math.max(ms, 0) / 3600000).toFixed(2);
    } catch {
      return "0.00";
    }
  };
  const openBreak = (e) => (e.breaks || []).find((b) => !b.end);

  return (
    <div className="space-y-6" data-testid="staff-tab">
      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-5 h-5 text-emerald-600" />
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">
            On the clock now ({onClock.length})
          </div>
        </div>
        {onClock.length === 0 ? (
          <div className="text-stone-400 text-sm">Nobody's clocked in.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {onClock.map((e) => (
              <div
                key={e.id}
                data-testid={`staff-on-clock-${e.employee_id}`}
                className={`rounded-lg border px-3 py-2 flex items-center justify-between ${openBreak(e) ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}
              >
                <div>
                  <div className="font-bold text-stone-800 text-sm flex items-center gap-2">
                    {e.employee_name}
                    {openBreak(e) && (
                      <span className="text-[9px] uppercase tracking-widest bg-amber-200 text-amber-900 rounded-full px-1.5 py-0.5">
                        On break since {fmtStarted(openBreak(e).start)}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-stone-500">
                    Since {fmtStarted(e.clock_in)} · ${e.pay_rate?.toFixed(2)}/hr
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`font-mono font-black text-sm ${openBreak(e) ? "text-amber-700" : "text-emerald-700"}`}>
                    {shiftHours(e)}h
                  </div>
                  <button
                    type="button"
                    onClick={() => clockOutNow(e)}
                    data-testid={`staff-on-clock-out-${e.employee_id}`}
                    className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-brand-red text-white hover:bg-red-800"
                    title="End this shift right now"
                  >
                    Clock out now
                  </button>
                  <button
                    type="button"
                    onClick={() => setFixEntry(e)}
                    data-testid={`staff-on-clock-fix-${e.employee_id}`}
                    className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border border-stone-300 bg-white text-stone-700 hover:border-brand-red hover:text-brand-red"
                    title="Close this shift or fix its times"
                  >
                    Fix times
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {fixEntry && (
        <TimeEntryEditor
          token={token}
          entry={fixEntry}
          onClose={() => setFixEntry(null)}
          onSaved={() => { setFixEntry(null); load(); setPunchRefresh((n) => n + 1); }}
        />
      )}

      <DailyPayCard token={token} refreshKey={punchRefresh} />

      <WeeklyPayCard token={token} />

      <RangePayCard token={token} />

      <TimeEntriesCard token={token} refreshKey={punchRefresh} onChanged={() => { load(); setPunchRefresh((n) => n + 1); }} />

      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <div className="font-black text-stone-800 text-sm uppercase tracking-widest mb-3">
          {editingId ? "Edit employee" : "Add employee"}
        </div>
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-5 gap-2">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name"
            data-testid="staff-form-name"
            className="col-span-2 px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm"
          />
          <input
            value={form.pin}
            onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
            placeholder="4-digit PIN"
            inputMode="numeric"
            maxLength={4}
            data-testid="staff-form-pin"
            className="px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm font-mono"
          />
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 15) })}
            placeholder="Phone (for My Hours login)"
            inputMode="tel"
            data-testid="staff-form-phone"
            className="px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm font-mono"
          />
          <div className="flex gap-2">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.pay_rate}
              onChange={(e) => setForm({ ...form, pay_rate: e.target.value })}
              placeholder="$/hr"
              data-testid="staff-form-rate"
              className="flex-1 px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm font-mono"
            />
          </div>
          <label className="sm:col-span-5 flex items-center gap-2 text-sm text-stone-700 cursor-pointer select-none" data-testid="staff-form-manager-label">
            <input type="checkbox" checked={!!form.is_manager} onChange={(e) => setForm({ ...form, is_manager: e.target.checked })} data-testid="staff-form-manager" className="w-4 h-4 accent-[#9B2226]" />
            <span className="font-bold">Manager</span>
            <span className="text-xs text-stone-500">— can sign into the Manager app at <span className="font-mono">/manager</span> with their PIN (clock, orders open/close, shift report, inventory, chat)</span>
          </label>
          <div className="sm:col-span-5 flex gap-2">
            <button
              type="submit"
              data-testid="staff-form-save"
              className="bg-brand-red text-white px-5 py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-red-800"
            >
              {editingId ? "Save changes" : "Add"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm({ name: "", pin: "", pay_rate: "", phone: "", is_manager: false });
                }}
                className="px-4 py-2 rounded-full border border-stone-300 text-xs uppercase tracking-widest font-bold text-stone-600"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">
            Employees ({employees.length})
          </div>
          <button
            onClick={async () => {
              try {
                const resp = await adminApi(token).get("/admin/payroll-export", {
                  responseType: "blob",
                });
                const url = URL.createObjectURL(resp.data);
                const a = document.createElement("a");
                a.href = url;
                const today = new Date().toISOString().slice(0, 10);
                a.download = `payroll_week_${today}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success("Payroll CSV downloaded");
              } catch {
                toast.error("Couldn't export payroll");
              }
            }}
            data-testid="staff-payroll-export-btn"
            className="text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full bg-brand-red text-white hover:bg-red-800 flex items-center gap-1"
            title="Download this week's hours per employee as CSV"
          >
            <Download className="w-3.5 h-3.5" />
            Payroll CSV
          </button>
        </div>
        {loading ? (
          <div className="p-6 text-center text-stone-400 text-sm">Loading…</div>
        ) : employees.length === 0 ? (
          <div className="p-6 text-center text-stone-400 text-sm">
            No employees yet. Add one above.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="staff-list-table">
            <thead>
              <tr className="bg-stone-50 text-stone-600 text-xs uppercase tracking-widest">
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">PIN</th>
                <th className="text-right px-4 py-2">Pay Rate</th>
                <th className="text-center px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp.id} className="border-t border-stone-100">
                  <td className="px-4 py-2 font-bold text-stone-800">{emp.name}{emp.is_manager && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-black uppercase tracking-widest" data-testid={`staff-manager-badge-${emp.id}`}>Manager</span>}</td>
                  <td className="px-4 py-2 font-mono text-stone-600">{emp.pin}<div className="text-[10px] text-stone-400">{emp.phone ? `…${emp.phone.slice(-4)}` : <span className="text-amber-600">no phone</span>}</div></td>
                  <td className="px-4 py-2 text-right font-mono">${emp.pay_rate.toFixed(2)}</td>
                  <td className="px-4 py-2 text-center">
                    {clockedInIds.has(emp.id) ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-black uppercase tracking-widest">
                        On Clock
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-500 font-black uppercase tracking-widest">
                        Off
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right space-x-1">
                    <button
                      onClick={() => edit(emp)}
                      data-testid={`staff-edit-${emp.id}`}
                      className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-brand-red"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => del(emp)}
                      data-testid={`staff-del-${emp.id}`}
                      className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-brand-red"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


/**
 * TipOnReaderSetupCard — a static checklist explaining how to make the
 * Square Bluetooth reader show its own tip screen. The deep-link cannot
 * force this behavior — it's a merchant-side toggle in the Square Register
 * app. Rendered inside the Site tab.
 */
function TipOnReaderSetupCard() {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl overflow-hidden"
      data-testid="tip-reader-setup-card"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-stone-50"
      >
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-brand-red" />
          <div className="text-left">
            <div className="font-black text-stone-800 text-sm uppercase tracking-widest">
              Tips on Reader — setup
            </div>
            <div className="text-[11px] text-stone-500">
              How to make the Bluetooth reader ask customers for a tip.
            </div>
          </div>
        </div>
        <div className="text-brand-red text-xs uppercase tracking-widest font-bold">
          {open ? "Hide" : "Show"}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 text-sm text-stone-700 border-t border-stone-100">
          <p className="text-[13px]">
            The tip prompt shown on your paired chip reader is a Square-side
            setting. Once enabled, our POS's "Ask on reader" mode (the default)
            will trigger it automatically after every card sale.
          </p>
          <ol className="space-y-2 text-[13px] list-decimal list-inside">
            <li>
              Open the <b>Square Register</b> app on the truck's iPad or Android tablet.
            </li>
            <li>
              Tap <b>≡ More</b> → <b>Settings</b> → <b>Checkout</b> → <b>Tips</b>.
            </li>
            <li>
              Toggle <b>Collect tips</b> ON.
            </li>
            <li>
              Under <b>Tip method</b>, choose <b>Chip &amp; contactless reader</b> (or
              <i> Card reader with tipping</i> depending on your Square version).
            </li>
            <li>
              Optional: pre-configure percentage buttons (e.g. 15% / 18% / 20%).
            </li>
            <li>
              Save. Next time our POS sends a sale, the tip screen appears on
              the reader — the customer taps their choice, then their card.
            </li>
          </ol>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900">
            <b>Cash tips</b> or <b>pre-agreed tips</b>? Use the "Enter manually" mode in the Charge dialog on the POS — that
            bakes the tip into the amount so the reader won't ask again.
          </div>
          <a
            href="https://squareup.com/help/us/en/article/5069-tipping-with-square"
            target="_blank"
            rel="noreferrer"
            className="text-brand-red text-[12px] font-bold underline"
          >
            Square's official help article →
          </a>
        </div>
      )}
    </div>
  );
}


/**
 * MenuTrendCard — 7-day sales sparkline per menu item, plus a "hot/cold"
 * badge computed by comparing the last 3 days vs the first 3 days.
 * Uses inline SVG so we don't need a chart lib.
 */
function MenuTrendCard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi(token).get("/admin/menu-trend?days=7");
        if (!cancelled) setData(data);
      } catch {
        // swallow — chart is progressive enhancement, not critical
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading || !data) return null;
  if (!data.items || data.items.length === 0) {
    return (
      <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="menu-trend-card">
        <div className="text-stone-400 text-sm text-center">
          No sales in the last 7 days yet — chart will appear once orders come in.
        </div>
      </div>
    );
  }

  const days = data.days;
  const visible = expanded ? data.items : data.items.slice(0, 5);

  // Sparkline: 120x36 viewBox, 6 days of gaps between 7 points.
  const spark = (series) => {
    const qs = series.map((s) => s.qty);
    const maxQ = Math.max(1, ...qs);
    const W = 120, H = 36;
    const step = W / Math.max(1, qs.length - 1);
    const pts = qs.map((q, i) => {
      const x = i * step;
      const y = H - (q / maxQ) * (H - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    return { path: `M ${pts.join(" L ")}`, points: pts, W, H };
  };

  // Trend score: last 3 days vs first 3 days average.
  const trendScore = (series) => {
    if (series.length < 6) return 0;
    const first = series.slice(0, 3).reduce((s, r) => s + r.qty, 0) / 3;
    const last = series.slice(-3).reduce((s, r) => s + r.qty, 0) / 3;
    if (first === 0 && last === 0) return 0;
    if (first === 0) return 1;
    return (last - first) / Math.max(first, 1);
  };

  const fmtDay = (iso) => {
    try {
      const d = new Date(iso + "T12:00:00");
      return d.toLocaleDateString([], { weekday: "short" });
    } catch { return iso; }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="menu-trend-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-brand-red" />
            Menu Item Trend · 7 days
          </div>
          <div className="text-[11px] text-stone-500 mt-0.5">
            {fmtDay(days[0])} → {fmtDay(days[days.length - 1])} · sorted by total qty
          </div>
        </div>
        {data.items.length > 5 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="menu-trend-expand-btn"
            className="text-brand-red text-xs uppercase tracking-widest font-bold hover:underline"
          >
            {expanded ? "Show top 5" : `Show all ${data.items.length}`}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {visible.map((row) => {
          const s = spark(row.series);
          const t = trendScore(row.series);
          const isHot = t >= 0.25;
          const isCold = t <= -0.25;
          return (
            <div
              key={row.name}
              data-testid={`menu-trend-row-${row.name}`}
              className="flex items-center gap-3 py-2 border-t border-stone-100 first:border-t-0"
            >
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-800 text-sm truncate">
                  {row.name}
                </div>
                <div className="text-[11px] text-stone-500">
                  {row.total_qty} sold · ${row.total_gross.toFixed(2)}
                </div>
              </div>
              <svg
                viewBox={`0 0 ${s.W} ${s.H}`}
                width={s.W}
                height={s.H}
                className="flex-shrink-0"
                aria-label={`${row.name} sales trend`}
              >
                <path
                  d={s.path}
                  fill="none"
                  stroke={isHot ? "#059669" : isCold ? "#DC2626" : "#78716c"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {s.points.map((p, i) => {
                  const [x, y] = p.split(",");
                  return (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r={i === s.points.length - 1 ? 3 : 1.5}
                      fill={isHot ? "#059669" : isCold ? "#DC2626" : "#78716c"}
                    />
                  );
                })}
              </svg>
              <div className="w-16 text-right">
                {isHot ? (
                  <span
                    data-testid={`menu-trend-badge-${row.name}`}
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-black uppercase tracking-widest"
                  >
                    <TrendingUp className="w-3 h-3" /> Hot
                  </span>
                ) : isCold ? (
                  <span
                    data-testid={`menu-trend-badge-${row.name}`}
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-black uppercase tracking-widest"
                  >
                    <TrendingDown className="w-3 h-3" /> Cold
                  </span>
                ) : (
                  <span
                    data-testid={`menu-trend-badge-${row.name}`}
                    className="text-[10px] text-stone-400 uppercase tracking-widest font-bold"
                  >
                    Steady
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * KDSTicketColorCard — lets the owner recolor the online-order tickets
 * (default school-bus yellow) and window-order tickets (default white)
 * on the Kitchen Display. Persisted on site_settings and picked up
 * live by KitchenPage.
 */
function KDSTicketColorCard({ form, setForm }) {
  if (!form) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const rows = [
    {
      title: "Online orders",
      subtitle: "Web/mobile pickup tickets on the KDS",
      bgKey: "kds_online_bg",
      textKey: "kds_online_text",
      bgDefault: "#FDD835",
      textDefault: "#000000",
    },
    {
      title: "Window orders (POS)",
      subtitle: "In-truck POS tickets on the KDS",
      bgKey: "kds_pos_bg",
      textKey: "kds_pos_text",
      bgDefault: "#ffffff",
      textDefault: "#111111",
    },
  ];
  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4"
      data-testid="kds-color-card"
    >
      <div>
        <div className="font-black text-stone-800 text-sm uppercase tracking-widest">
          KDS Ticket Colors
        </div>
        <div className="text-[11px] text-stone-500">
          Recolor the kitchen display tickets so channels are easy to spot at a glance.
        </div>
      </div>
      {rows.map((r) => {
        const bg = form[r.bgKey] || r.bgDefault;
        const text = form[r.textKey] || r.textDefault;
        return (
          <div key={r.bgKey} className="border border-stone-200 rounded-xl p-3 grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
            <div>
              <div className="font-bold text-stone-800 text-sm">{r.title}</div>
              <div className="text-[11px] text-stone-500">{r.subtitle}</div>
            </div>
            <label className="flex items-center gap-2 text-xs text-stone-600">
              <span className="w-16 shrink-0">Background</span>
              <input
                type="color"
                value={bg}
                onChange={(e) => set(r.bgKey, e.target.value)}
                data-testid={`kds-color-${r.bgKey}`}
                className="w-10 h-8 rounded"
              />
              <input
                type="text"
                value={bg}
                onChange={(e) => set(r.bgKey, e.target.value)}
                className="w-24 px-2 py-1 rounded border border-stone-300 text-xs font-mono"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-stone-600">
              <span className="w-16 shrink-0">Text</span>
              <input
                type="color"
                value={text}
                onChange={(e) => set(r.textKey, e.target.value)}
                data-testid={`kds-color-${r.textKey}`}
                className="w-10 h-8 rounded"
              />
              <input
                type="text"
                value={text}
                onChange={(e) => set(r.textKey, e.target.value)}
                className="w-24 px-2 py-1 rounded border border-stone-300 text-xs font-mono"
              />
            </label>
            <div className="sm:col-span-3 rounded-lg px-3 py-2 border" style={{ background: bg, color: text, borderColor: text + "44" }}>
              <div className="font-display text-brand-red text-lg leading-none">
                Preview #B123
              </div>
              <div className="text-[10px] uppercase tracking-widest font-bold">
                {r.title === "Online orders" ? "ONLINE PICKUP" : "WINDOW · Beeper #12"}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


/**
 * ChangeCashierPasscodeCard — admin-only card that lets the owner rotate
 * the passcode staff use to enter the POS. Backend enforces:
 *   - 6+ characters (softer than admin's 12-char rule; tablet-typed daily)
 *   - Must not match the admin passcode
 *   - Only admins can hit the endpoint (cashier tokens get 403)
 */
function ChangeCashierPasscodeCard({ token }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasCustom, setHasCustom] = useState(false);

  useEffect(() => {
    if (!token) return;
    adminApi(token)
      .get("/admin/status")
      .then((r) => setHasCustom(!!r.data.has_custom_cashier_passcode))
      .catch(() => {});
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("Passcode must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passcodes don't match");
      return;
    }
    setSaving(true);
    try {
      await adminApi(token).post("/admin/change-cashier-passcode", {
        new_passcode: next,
      });
      toast.success("Cashier passcode updated");
      setNext("");
      setConfirm("");
      setShow(false);
      setHasCustom(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Change failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3"
      data-testid="cashier-passcode-card"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">
            Cashier Passcode
          </div>
          <div className="text-[11px] text-stone-500">
            Passcode staff type on the tablet to open the POS. Admin-only change.
            {hasCustom
              ? " · Custom passcode is set."
              : " · Still on the default — change it now."}
          </div>
        </div>
        {!show && (
          <button
            type="button"
            onClick={() => setShow(true)}
            data-testid="cashier-passcode-change-btn"
            className="bg-brand-red text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold hover:bg-red-800"
          >
            Change
          </button>
        )}
      </div>
      {show && (
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
              New cashier passcode (6+ chars, cannot equal admin passcode)
            </span>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="cashier-passcode-new"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
              Confirm new cashier passcode
            </span>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="cashier-passcode-confirm"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="cashier-passcode-save"
              className="bg-brand-red text-white px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold disabled:opacity-50 hover:bg-red-800"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => { setShow(false); setNext(""); setConfirm(""); }}
              className="px-4 py-2 rounded-full border border-stone-300 text-xs uppercase tracking-widest font-bold text-stone-600"
            >
              Cancel
            </button>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900">
            Every tablet currently logged in as cashier will need to log in
            again with the new passcode after you save.
          </div>
        </form>
      )}
    </div>
  );
}


/**
 * StaffClockPunchPad — inline 4-digit PIN pad on the Staff tab. Any
 * employee (cook, cashier, dishwasher…) can punch their PIN here to
 * toggle their clock state without needing to log out of the POS or find
 * the login-screen version. Uses the same public /api/pos/clock-toggle
 * endpoint (no admin token required — PINs are the credential).
 */
function StaffClockPunchPad({ onPunched }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastAction, setLastAction] = useState(null);

  const submit = useCallback(async (candidate) => {
    if (candidate.length !== 4) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pos/clock-toggle", { pin: candidate });
      setLastAction(data);
      const msg = data.action === "in"
        ? `Welcome, ${data.employee_name} — Clocked in`
        : `Bye, ${data.employee_name} — ${data.hours}h logged`;
      toast.success(msg, { duration: 3000 });
      if (onPunched) onPunched();
      // Clear the pad after a short delay so the manager can watch the toast.
      setTimeout(() => { setPin(""); }, 600);
      // Fade the "last action" indicator after 8s.
      setTimeout(() => setLastAction(null), 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PIN not recognised");
      setPin("");
    } finally {
      setBusy(false);
    }
  }, [onPunched]);

  useEffect(() => {
    if (pin.length === 4 && !busy) submit(pin);
    // eslint-disable-next-line
  }, [pin]);

  const press = (n) => setPin((p) => (p.length >= 4 ? p : p + n));
  const back = () => setPin((p) => p.slice(0, -1));

  return (
    <div
      className="bg-white border-2 border-brand-red rounded-2xl p-5"
      data-testid="staff-clock-punch-pad"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-brand-red" />
          <div>
            <div className="font-black text-stone-800 text-sm uppercase tracking-widest">
              Clock In / Out
            </div>
            <div className="text-[11px] text-stone-500">
              Any staff member punches their 4-digit PIN — same PIN toggles in and out.
            </div>
          </div>
        </div>
        {lastAction && (
          <div
            data-testid="staff-clock-punch-result"
            className={`text-right text-xs font-black uppercase tracking-widest ${
              lastAction.action === "in" ? "text-emerald-600" : "text-brand-red"
            }`}
          >
            <div>
              {lastAction.action === "in"
                ? `✓ ${lastAction.employee_name} IN`
                : `✓ ${lastAction.employee_name} OUT`}
            </div>
            {lastAction.action === "out" && (
              <div className="font-mono text-[11px] text-stone-500 mt-0.5">
                {lastAction.hours}h logged
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-4 items-start">
        <div className="max-w-[260px] mx-auto sm:mx-0 w-full">
          <div
            className="flex justify-center gap-2 mb-3"
            data-testid="staff-clock-punch-dots"
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full border-2 transition-colors ${
                  pin.length > i ? "bg-brand-red border-brand-red" : "border-stone-300"
                }`}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button
                key={n}
                onClick={() => press(String(n))}
                disabled={busy}
                data-testid={`staff-clock-punch-key-${n}`}
                className="py-3 rounded-xl bg-stone-100 hover:bg-brand-yellow text-xl font-black text-stone-800 disabled:opacity-40 transition-colors"
              >
                {n}
              </button>
            ))}
            <button
              onClick={back}
              disabled={busy || pin.length === 0}
              data-testid="staff-clock-punch-key-back"
              className="py-3 rounded-xl bg-stone-100 hover:bg-stone-200 text-xs font-bold text-stone-600 uppercase tracking-widest disabled:opacity-40"
            >
              ⌫
            </button>
            <button
              onClick={() => press("0")}
              disabled={busy}
              data-testid="staff-clock-punch-key-0"
              className="py-3 rounded-xl bg-stone-100 hover:bg-brand-yellow text-xl font-black text-stone-800 disabled:opacity-40 transition-colors"
            >
              0
            </button>
            <button
              onClick={() => setPin("")}
              disabled={busy || pin.length === 0}
              data-testid="staff-clock-punch-key-clear"
              className="py-3 rounded-xl bg-stone-100 hover:bg-stone-200 text-xs font-bold text-stone-600 uppercase tracking-widest disabled:opacity-40"
            >
              Clear
            </button>
          </div>
        </div>
        <div className="text-[12px] text-stone-500 leading-relaxed sm:pl-4 sm:border-l sm:border-stone-100">
          <p className="mb-2">
            <b>Tip:</b> Hand the tablet to a staff member to punch their PIN.
            Wrong PIN just clears — no lockout.
          </p>
          <p className="mb-2">
            Currently on shift → see the <b>On the clock now</b> panel below.
          </p>
          <p>
            Manage staff (PIN + pay rate) → the <b>Employees</b> table.
          </p>
        </div>
      </div>
    </div>
  );
}


/**
 * FeaturedItemsCard — manage the homepage image carousel. Owner uploads
 * pictures (compressed by Pillow server-side), adds optional captions,
 * reorders, and deletes. `featured_items` is a JSON array of
 * `{image_url, caption}` stored on site_settings. The carousel on the
 * public homepage is invisible whenever this list is empty.
 */
function FeaturedItemsCard({ token, form, setForm }) {
  const [menu, setMenu] = useState([]);

  // Load the menu once so each row can offer a "Link to menu item" dropdown.
  useEffect(() => {
    let cancelled = false;
    api.get("/menu")
      .then(({ data }) => { if (!cancelled) setMenu(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!form) return null;
  const items = Array.isArray(form.featured_items) ? form.featured_items : [];

  const update = (next) => setForm({ ...form, featured_items: next });
  const addUploaded = (url) => update([...items, { image_url: url, caption: "", item_id: "" }]);
  const setCaption = (i, caption) => {
    const next = items.slice();
    next[i] = { ...next[i], caption };
    update(next);
  };
  const setItemId = (i, item_id) => {
    const next = items.slice();
    next[i] = { ...next[i], item_id };
    update(next);
  };
  const remove = (i) => update(items.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
  };

  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3"
      data-testid="featured-items-card"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">
            Featured Items Carousel
          </div>
          <div className="text-[11px] text-stone-500">
            Photos here appear as a rotating slideshow on the homepage.
            Empty → the whole slideshow is hidden.
          </div>
        </div>
        <MediaUploadButton
          token={token}
          kind="image"
          label="Upload photo"
          onUploaded={addUploaded}
        />
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-stone-400 text-sm border-2 border-dashed border-stone-200 rounded-xl">
          No featured items yet. Upload a photo above and hit Save.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div
              key={i}
              data-testid={`featured-row-${i}`}
              className="flex items-center gap-3 border border-stone-200 rounded-xl p-2"
            >
              <img
                src={it.image_url}
                alt={it.caption || `Featured ${i + 1}`}
                className="w-24 h-16 object-cover rounded-lg bg-stone-100 shrink-0"
              />
              <div className="flex-1 min-w-0 space-y-1">
                <input
                  type="text"
                  placeholder="Caption (optional)"
                  value={it.caption || ""}
                  onChange={(e) => setCaption(i, e.target.value)}
                  data-testid={`featured-caption-${i}`}
                  maxLength={80}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm"
                />
                <select
                  value={it.item_id || ""}
                  onChange={(e) => setItemId(i, e.target.value)}
                  data-testid={`featured-item-picker-${i}`}
                  className="w-full px-3 py-1.5 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-xs text-stone-600"
                >
                  <option value="">🔗 Link to menu item (optional)</option>
                  {menu.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} — ${(m.price || 0).toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  data-testid={`featured-up-${i}`}
                  className="p-1.5 rounded hover:bg-stone-100 text-stone-500 disabled:opacity-30"
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === items.length - 1}
                  data-testid={`featured-down-${i}`}
                  className="p-1.5 rounded hover:bg-stone-100 text-stone-500 disabled:opacity-30"
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  data-testid={`featured-remove-${i}`}
                  className="p-1.5 rounded hover:bg-red-50 text-brand-red"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          <div className="text-[11px] text-stone-500 pt-1">
            Reorder with ↑/↓. Changes apply after you hit <b>Save</b> at the bottom.
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * PosLayoutCard — Admin controls for the register UI:
 *   • Grid columns (3-6)
 *   • Per-item color + "hide from POS" table
 *
 * Per-item edits fire /menu/{id} PUT immediately (no dirty state) so the
 * cashier's next poll on /menu (or a page reload) reflects the change.
 * The category color picker already lives in `MenuCategoryColorsCard`.
 */
const POS_TILE_PALETTE_ADMIN = [
  "#C0392B", "#E67E22", "#F1C40F", "#27AE60",
  "#16A085", "#3498DB", "#8E44AD", "#D81B60",
  "#2C3E50", "#7F8C8D",
];

function PosLayoutCard({ token, form, setForm }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const cols = Math.min(6, Math.max(3, form?.pos_grid_columns || 4));

  const refresh = async () => {
    try {
      const { data } = await adminApi(token).get("/menu");
      setItems(data.filter((m) => m.category !== "Add-Ons"));
    } catch {
      /* transient */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const patchItem = async (id, patch) => {
    setItems((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    try {
      await adminApi(token).put(`/menu/${id}`, patch);
    } catch {
      toast.error("Couldn't save — refresh and try again");
      await refresh();
    }
  };

  const filtered = items.filter((m) =>
    !q.trim()
      ? true
      : `${m.name} ${m.category}`.toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <div
      className="bg-white border border-stone-200 rounded-xl p-4 space-y-4"
      data-testid="admin-pos-layout-card"
    >
      <div>
        <div className="text-2xl font-display text-brand-red">POS Layout</div>
        <div className="text-xs text-stone-500 mt-0.5">
          Configure how the register grid looks. Per-category colors live in "Menu Categories" above.
          <br />
          Cashiers can also long-press any tile in the POS to change these on the fly.
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-widest font-bold text-stone-600">
          Grid columns
        </div>
        <div className="flex gap-1">
          {[3, 4, 5, 6].map((n) => (
            <button
              key={n}
              onClick={() => setForm({ ...form, pos_grid_columns: n })}
              data-testid={`pos-grid-cols-${n}`}
              className={`w-10 h-10 rounded-lg border-2 font-black text-sm ${
                cols === n
                  ? "border-brand-red bg-brand-red text-white"
                  : "border-stone-300 text-stone-600 hover:border-brand-red"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-stone-500">
          Tap Save Site Content at the bottom of the page to apply.
        </div>
      </div>

      <div className="pt-2">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest font-bold text-stone-600">
            Item Tiles
          </div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            data-testid="pos-layout-search"
            className="px-3 py-1.5 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
          />
        </div>
        {loading ? (
          <div className="text-center py-4 text-sm text-stone-400">Loading…</div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto border border-stone-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 sticky top-0">
                <tr className="text-[10px] uppercase tracking-widest text-stone-500">
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-left px-3 py-2">Color</th>
                  <th className="text-center px-3 py-2">Hide</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr
                    key={m.id}
                    data-testid={`pos-layout-row-${m.id}`}
                    className="border-t border-stone-100"
                  >
                    <td className="px-3 py-2">
                      <div className="font-bold text-stone-800">{m.name}</div>
                      <div className="text-[10px] text-stone-500 uppercase tracking-widest">
                        {m.category}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        <button
                          onClick={() => patchItem(m.id, { pos_color: null })}
                          className={`text-[9px] font-black uppercase px-2 py-1 rounded border-2 ${
                            !m.pos_color
                              ? "border-brand-red bg-brand-red text-white"
                              : "border-stone-300 text-stone-600"
                          }`}
                          data-testid={`pos-layout-color-auto-${m.id}`}
                          title="Use category color"
                        >
                          Auto
                        </button>
                        {POS_TILE_PALETTE_ADMIN.map((c) => (
                          <button
                            key={c}
                            onClick={() => patchItem(m.id, { pos_color: c })}
                            style={{ backgroundColor: c }}
                            className={`w-6 h-6 rounded border-2 ${
                              m.pos_color === c ? "border-black" : "border-white/40"
                            }`}
                            data-testid={`pos-layout-color-${m.id}-${c}`}
                            title={c}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={!!m.pos_hidden}
                        onChange={(e) =>
                          patchItem(m.id, { pos_hidden: e.target.checked })
                        }
                        data-testid={`pos-layout-hide-${m.id}`}
                        className="w-4 h-4 accent-brand-red"
                      />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center py-6 text-stone-400 text-sm">
                      No matching items
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

